if (!customElements.get('chat-assistant')) {
  class ChatAssistant extends HTMLElement {
    constructor() {
      super();

      this.storefrontToken = this.getAttribute('data-storefront-token');
      this.shopUrl = this.getAttribute('data-shop-url');
      this.lang = this.getAttribute('data-lang') || 'en';
      this.isArabic = this.lang === 'ar';
      this.langPrefix = this.isArabic ? '/ar' : '';
      this.pageType = this.getAttribute('data-page-type') || '';
      // Where the chat was opened (product/collection page) - sent to /api/chat as a hint
      this.pageContext = { page: this.pageType };
      const ctxKind = this.getAttribute('data-context-kind');
      const ctxHandle = this.getAttribute('data-context-handle');
      const ctxTitle = this.getAttribute('data-context-title');
      if ((ctxKind === 'product' || ctxKind === 'collection') && ctxKind === this.pageType && ctxHandle && ctxTitle) {
        this.pageContext[ctxKind] = { handle: ctxHandle, title: ctxTitle };
      }
      this.contextProductId = ctxKind === 'product' ? (this.getAttribute('data-context-id') || '') : '';
      // better-sell app proxy (stores with the app installed): conversion rank, picks, audience, pairs.
      // Shopify signs proxy calls and adds the logged-in customer id itself; the
      // theme only knows whether someone is logged in.
      this.betterSell = this.getAttribute('data-better-sell') || '';
      this.isLoggedIn = this.getAttribute('data-customer') === '1';
      this.audience = null;
      this.pairsShownFor = new Set();

      // Attribute reader that ignores Shopify's missing-translation sentinel
      // ("Translation missing: en.general.chat.x") so the fallback applies
      const attr = (name, fallback) => {
        const v = this.getAttribute(name);
        return v && !v.startsWith('Translation missing') ? v : fallback;
      };

      // UI strings from data attributes
      this.strings = {
        welcome: this.getAttribute('data-welcome'),
        placeholder: this.getAttribute('data-placeholder'),
        thinking: this.getAttribute('data-thinking'),
        error: this.getAttribute('data-error'),
        rateLimited: this.getAttribute('data-rate-limited'),
        offline: this.getAttribute('data-offline'),
        noProducts: this.getAttribute('data-no-products'),
        similarProducts: this.getAttribute('data-similar-products'),
        soldOut: this.getAttribute('data-sold-out-label') || 'Sold out',
        chooseSection: this.getAttribute('data-choose-section') || 'Choose a section:',
        chooseType: this.getAttribute('data-choose-type') || 'Choose a type:',
        browseAll: this.getAttribute('data-browse-all') || 'Browse all',
        chipTrack: this.getAttribute('data-chip-track') || 'Track my order',
        chipDeals: this.getAttribute('data-chip-deals') || "Amazing prices",
        chipDealsCollection: this.getAttribute('data-chip-deals-collection') || 'amazing-prices',
        chipHelp: this.getAttribute('data-chip-help') || 'Help me choose',
        chipSupportLabel: this.getAttribute('data-chip-support-label') || 'Talk to support',
        chipMore: this.getAttribute('data-chip-more') || 'Show more',
        chipDifferent: this.getAttribute('data-chip-different') || 'Different style',
        chipCheaper: this.getAttribute('data-chip-cheaper') || 'Under 500 EGP',
        chipPicks: this.isArabic
          ? (this.isLoggedIn ? 'مختارات لك' : 'الأكثر مبيعاً')
          : (this.isLoggedIn ? 'Picked for you' : 'Best sellers'),
        teaserProduct: this.getAttribute('data-teaser-product') || '',
        teaserCart: this.getAttribute('data-teaser-cart') || '',
        teaserCollection: this.getAttribute('data-teaser-collection') || '',
        teaserHome: this.getAttribute('data-teaser-home') || '',
        escalationPrompt: this.getAttribute('data-escalation-prompt') || '',
        escalationButton: this.getAttribute('data-escalation-button') || 'Chat on WhatsApp',
        supportMessage: this.getAttribute('data-support-message') || 'You can reach our support team at 19785 or chat with us on WhatsApp.',
        trackPrompt: this.getAttribute('data-track-prompt') || 'Please enter your 8-digit order number (starts with 5000) to track your shipment.',
        credentialPrompt: this.getAttribute('data-credential-prompt') || 'Please enter the email or phone number used for this order:',
        returnsMessage: this.getAttribute('data-returns-message') || 'Exchanges are available in Al Nasser stores. You can submit a return request here:',
        returnsUrlLabel: this.getAttribute('data-returns-url-label') || 'Returns & Exchanges',
        helpChooseMessage: this.getAttribute('data-help-choose-message') || 'What are you looking for? Browse our categories or tell me what you need!',
        notCarried: this.getAttribute('data-not-carried') || "Sorry, we don't carry this brand. Check out our similar brands!",
        storeInfo: this.getAttribute('data-store-info') || 'Visit any of our stores across Egypt, or shop online with next-day delivery for orders.',
        storeLocatorLabel: this.getAttribute('data-store-locator-label') || 'Find a store',
        deliveryMessage: this.getAttribute('data-delivery-message') || 'We offer next-day delivery to our delivery areas, free on orders of EGP 500 or more.',
        showMoreMessage: this.getAttribute('data-show-more-message') || 'Here are more results:',
        noPreviousSearch: this.getAttribute('data-no-previous-search') || "Tell me what you're looking for and I'll find it for you!",
        browseMessage: this.getAttribute('data-browse-message') || 'Here are our categories — tap to explore!',
        youreWelcome: this.getAttribute('data-youre-welcome') || "You're welcome!",
        goodbye: this.getAttribute('data-goodbye') || 'Goodbye!',
        addedToCart: this.getAttribute('data-added-to-cart') || 'Added!',
        viewItem: this.getAttribute('data-view-item') || 'View product'
      };

      // Store-specific config (from data attributes, no hardcoding)
      this.storeCountry = attr('data-store-country', 'Egypt');
      this.currency = this.getAttribute('data-currency') || 'EGP';
      this.currencySymbol = attr('data-currency-symbol', this.currency);
      const budgetRaw = parseInt(attr('data-budget-amount', '500'), 10);
      this.budgetAmount = Number.isFinite(budgetRaw) ? budgetRaw : 500;
      this.waNumber = attr('data-whatsapp', '201025366663');
      this.returnsUrl = attr('data-returns-url', 'https://online.alnasser.net/eg/returns-portal');
      this.storeLocatorUrl = attr('data-store-locator-url', '/pages/store-locator');
      this.trackApiUrl = attr('data-track-api-url', 'https://online.alnasser.net/api/eg/track/access');
      this.trackUrlPrefix = attr('data-track-url-prefix', 'https://online.alnasser.net/eg/order-tracking');

      // 3-level catalog from Liquid-generated JSON
      this.catalog = this.loadCatalog();
      this.fillMissingCatalogImages();

      // Synonym support
      this.synonymMap = null;
      this.synonymsLoaded = false;
      this.SYNONYMS_CACHE_KEY = 'search-synonyms';
      this.SYNONYMS_CACHE_TTL = 30 * 60 * 1000; // 30 min (shared with predictive-search)

      // DOM
      this.fab = this.querySelector('.chat-fab');
      this.overlay = this.querySelector('.chat-overlay');
      this.backdrop = this.querySelector('.chat-backdrop');
      this.teaser = this.querySelector('.chat-teaser');
      this.teaserText = this.querySelector('.chat-teaser__text');
      this.teaserClose = this.querySelector('.chat-teaser__close');
      this.closeBtn = this.querySelector('.chat-header__close');
      this.messagesEl = this.querySelector('.chat-messages');
      this.input = this.querySelector('.chat-input');
      this.sendBtn = this.querySelector('.chat-send-btn');

      // Escape parent stacking context (header section z-index: 30)
      // Fixed elements need to be direct children of body for z-index to work globally
      document.body.appendChild(this.fab);
      document.body.appendChild(this.overlay);
      if (this.backdrop) document.body.appendChild(this.backdrop);
      if (this.teaser) document.body.appendChild(this.teaser);

      // State
      this.conversationHistory = [];
      this.turn = 0; // bumped on every customer message; late async cards check it
      this.isProcessing = false;
      this.isOpen = false;
      this.hasGreeted = false;
      this.pendingOrderNumber = null;
      this.lastSearchTerms = null;
      this.lastQuery = null; // last product query parsed by parseQuery (for typed refinements)

      // #7 — WhatsApp escalation tracking
      this.msgsSinceInteraction = 0;
      this.userMessages = [];

      // #6 — Proactive teaser
      this.teaserShown = false;
      this.teaserTimer = null;

      this.bindEvents();
      this.initSynonyms();
      this.initProactiveTeaser();
      this.applyWebViewInsets();
      this.updateOnlineStatus();

    }

    updateOnlineStatus() {
      const statusEl = this.overlay.querySelector('.chat-header__status');
      if (!statusEl) return;
      // Store hours are local to Egypt. Africa/Cairo observes DST (UTC+2 / UTC+3),
      // so a fixed offset drifts by an hour for half the year — ask Intl instead.
      const now = new Date();
      let localHour;
      try {
        localHour = Number(new Intl.DateTimeFormat('en-US', {
          timeZone: 'Africa/Cairo', hour: 'numeric', hour12: false
        }).formatToParts(now).find(function (p) { return p.type === 'hour'; }).value) % 24;
      } catch (e) {
        // No Intl time zone support. Cairo is UTC+2, UTC+3 under DST (roughly
        // late Apr - late Oct), so approximate by month rather than pinning one
        // offset and being an hour out for half the year.
        var m = now.getUTCMonth();
        localHour = (now.getUTCHours() + (m >= 3 && m <= 9 ? 3 : 2)) % 24;
      }
      const isOnline = localHour >= 8 && localHour < 22;
      statusEl.textContent = isOnline
        ? (statusEl.dataset.onlineText || 'Online now')
        : (statusEl.dataset.offlineText || 'Available 8 AM – 10 PM');
      statusEl.dataset.isOffline = !isOnline;
    }

    // --- Catalog loading ---

    loadCatalog() {
      try {
        const el = document.getElementById('chat-catalog-data');
        if (el) {
          const data = JSON.parse(el.textContent);
          return Array.isArray(data) ? data : [];
        }
      } catch (e) {
        // Silent fail — catalog browsing will be unavailable
      }
      return [];
    }

    /**
     * For catalog items missing images, fetch collection image via Storefront API.
     * Falls back to first product's featured image if collection has no image.
     * Runs async in background — images fill in before user opens catalog.
     */
    async fillMissingCatalogImages() {
      if (!this.catalog.length || !this.storefrontToken || !this.shopUrl) return;

      // Group siblings (items sharing a parent) so fallback images dedup per sibling-set
      const groups = [];
      const walkGroups = (items, depth) => {
        const siblingsMissing = [];
        for (const item of items) {
          const hasImage = depth === 1
            ? (item.art || item.banner || item.image)
            : (item.art || item.banner || item.icon);
          if (!hasImage && item.handle) {
            siblingsMissing.push(item);
          }
          if (item.children?.length && depth < 2) {
            walkGroups(item.children, depth + 1);
          }
        }
        if (siblingsMissing.length) groups.push(siblingsMissing);
      };
      walkGroups(this.catalog, 1);

      if (!groups.length) return;

      const handles = [...new Set(groups.flat().map(i => i.handle))];
      // Fetch 10 products per collection so we have alternates for dedup
      const fetches = handles.map(async (handle) => {
        try {
          const resp = await fetch(`${this.shopUrl}/api/2024-01/graphql.json`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': this.storefrontToken
            },
            body: JSON.stringify({
              query: `{
                collectionByHandle(handle: "${handle}") {
                  image { url }
                  products(first: 10) {
                    nodes {
                      id
                      featuredImage { url }
                    }
                  }
                }
              }`
            })
          });
          const json = await resp.json();
          const col = json.data?.collectionByHandle;
          return {
            handle,
            collectionImage: col?.image?.url || null,
            products: (col?.products?.nodes || [])
              .map(p => ({ id: p.id, url: p.featuredImage?.url || null }))
              .filter(p => p.id && p.url)
          };
        } catch {
          return { handle, collectionImage: null, products: [] };
        }
      });

      const fetchMap = {};
      for (const r of await Promise.all(fetches)) {
        fetchMap[r.handle] = r;
      }

      // Dedup fallback images within each sibling group; if all are used, allow duplicate
      for (const group of groups) {
        const usedProductIds = new Set();
        for (const item of group) {
          const data = fetchMap[item.handle];
          if (!data) continue;
          let url = null;
          if (data.collectionImage) {
            url = data.collectionImage;
          } else {
            const fresh = data.products.find(p => !usedProductIds.has(p.id));
            if (fresh) {
              usedProductIds.add(fresh.id);
              url = fresh.url;
            } else if (data.products[0]) {
              url = data.products[0].url;
            }
          }
          if (url) {
            item.image = item.image || url;
            item.icon = item.icon || url;
          }
        }
      }
    }

    // --- Synonym support (shared cache with predictive-search) ---

    initSynonyms() {
      const enabled = this.getAttribute('data-synonyms-enabled');
      if (enabled !== 'true') return;

      try {
        const cached = sessionStorage.getItem(this.SYNONYMS_CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed.timestamp && (Date.now() - parsed.timestamp) < this.SYNONYMS_CACHE_TTL) {
            this.synonymMap = parsed.data;
            this.synonymsLoaded = true;
            return;
          }
        }
      } catch (e) {
        // Cache read failed
      }

      const endpoint = this.getAttribute('data-synonyms-endpoint');
      const apiKey = this.getAttribute('data-synonyms-api-key');
      if (!endpoint || !apiKey) return;

      fetch(`${endpoint}?apiKey=${encodeURIComponent(apiKey)}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.ok && data.synonyms) {
            this.synonymMap = data.synonyms;
            this.synonymsLoaded = true;
            try {
              sessionStorage.setItem(this.SYNONYMS_CACHE_KEY, JSON.stringify({
                data: data.synonyms,
                timestamp: Date.now()
              }));
            } catch (e) {
              // Storage full
            }
          }
        })
        .catch(() => {
          // Silent fail — synonym fallback is non-critical
        });
    }

    lookupSynonyms(term) {
      if (!this.synonymMap || !term) return null;
      return this.synonymMap[term.trim()] || null;
    }

    // --- Events ---

    bindEvents() {
      this.fab.addEventListener('click', () => this.open());
      this.closeBtn.addEventListener('click', () => this.close());
      this.backdrop?.addEventListener('click', () => this.close());  /* optional chaining guard */
      this.sendBtn.addEventListener('click', () => this.handleSend());
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.handleSend();
        }
      });

      // #6 — Teaser events
      if (this.teaser) {
        this.teaser.addEventListener('click', (e) => {
          if (e.target === this.teaserClose || e.target.closest('.chat-teaser__close')) {
            this.dismissTeaser();
          } else {
            this.dismissTeaser();
            this.open();
          }
        });
      }

      // Mobile keyboard: sync overlay to visual viewport (iOS only).
      // Android WebView uses interactive-widget=resizes-content instead (set in applyWebViewInsets).
      if (window.visualViewport && !/android/i.test(navigator.userAgent)) {
        const syncToViewport = () => {
          if (!this.isOpen) return;
          const vv = window.visualViewport;
          const keyboardUp = window.innerHeight - vv.height > 50;
          if (keyboardUp) {
            this.overlay.style.height = vv.height + 'px';
            this.overlay.style.top = vv.offsetTop + 'px';
            this.overlay.style.bottom = 'auto';
            this.scrollToBottom();
          } else {
            this.overlay.style.height = '';
            this.overlay.style.top = '';
            this.overlay.style.bottom = '';
          }
        };
        window.visualViewport.addEventListener('resize', syncToViewport);
        window.visualViewport.addEventListener('scroll', syncToViewport);
      }
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.fab.hidden = true;
      this.dismissTeaser();

      this.overlay.setAttribute('aria-hidden', 'false');
      this.backdrop?.setAttribute('aria-hidden', 'false');  /* guard against null */
      document.body.style.overflowY = 'hidden';  /* only Y — never touch overflow-x (Safari/OverlayScrollbars) */
      // Skip auto-focus on Android WebView — keyboard immediately covers the chat
      if (!document.documentElement.classList.contains('is-android-webview')) {
        this.input.focus();
      }

      if (!this.hasGreeted) {
        this.hasGreeted = true;

        this.addAssistantMessage(this.strings.welcome);

        this.showCatalogLevel(this.catalog, 1, []);
        this.addQuickReplies([
          ...(this.betterSell ? [this.strings.chipPicks] : []),
          this.strings.chipTrack,
          this.strings.chipDeals,
          this.strings.chipHelp,
          this.strings.chipSupportLabel
        ]);
        this.loadAudience();
      }
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.input.blur();               // dismiss iOS keyboard + reset any Safari zoom

      // Determine animation based on screen size and direction
      const isMobile = window.innerWidth < 1024;
      const isRTL = document.dir === 'rtl' || document.documentElement.dir === 'rtl';
      let animationName = 'chatOverlayOut';

      if (!isMobile) {
        animationName = isRTL ? 'chatSlideOutLeft' : 'chatSlideOutRight';
      }

      // Animate out, then hide
      this.overlay.style.animation = `${animationName} 0.25s ease-in forwards`;

      const onAnimationEnd = () => {
        this.overlay.removeEventListener('animationend', onAnimationEnd);
        this.overlay.setAttribute('aria-hidden', 'true');  // triggers display: none
        this.overlay.style.animation = '';
        this.overlay.style.bottom = '';
      };

      this.overlay.addEventListener('animationend', onAnimationEnd);

      // Fallback in case animationend doesn't fire
      setTimeout(onAnimationEnd, 300);

      this.backdrop?.setAttribute('aria-hidden', 'true');
      document.body.style.overflowY = '';
      this.fab.hidden = false;
    }

    // --- Message rendering ---

    addUserMessage(text) {
      const bubble = document.createElement('div');
      bubble.className = 'chat-bubble chat-bubble--user';
      bubble.textContent = text;
      this.messagesEl.appendChild(bubble);
      this.scrollToBottom();
    }

    addAssistantMessage(text) {
      const bubble = document.createElement('div');
      bubble.className = 'chat-bubble chat-bubble--assistant';
      let safe = this.escapeHtml(text);
      // Make URLs clickable
      safe = safe.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>');
      // Fix phone numbers reversed in RTL — only outside <a> tags
      safe = safe.replace(/(<a[^>]*>.*?<\/a>)|(\+?\d[\d\s\-().]{4,}\d)/g, (m, link, phone) => {
        if (link) return link;
        return '\u200E' + phone + '\u200E';
      });
      bubble.innerHTML = safe;
      this.messagesEl.appendChild(bubble);
      this.scrollToBottom();
      return bubble;
    }

    addQuickReplies(replies) {
      if (!replies || replies.length === 0) return;

      const container = document.createElement('div');
      container.className = 'chat-quick-replies';

      replies.forEach((reply) => {
        const label = typeof reply === 'object' ? (reply.label || reply.text || reply.value || JSON.stringify(reply)) : String(reply);

        if (label === this.strings.chipDeals) {
          const a = document.createElement('a');
          a.className = 'chat-chip';
          a.href = `${this.langPrefix}/collections/${this.strings.chipDealsCollection}`;
          a.textContent = label;
          container.appendChild(a);
          return;
        }

        const btn = document.createElement('button');
        btn.className = 'chat-chip';
        btn.textContent = label;
        btn.addEventListener('click', () => {
          container.querySelectorAll('.chat-chip').forEach((c) => (c.disabled = true));
          this.handleQuickReply(typeof reply === 'object' && reply.query ? reply.query : label, label);
        });
        container.appendChild(btn);
      });

      this.messagesEl.appendChild(container);
      this.scrollToBottom();
    }

    // --- #9 — CTA button ---

    addCtaButton(cta) {
      if (!cta || !cta.label || !cta.url) return;
      const a = document.createElement('a');
      a.className = 'chat-cta-btn';
      a.href = cta.url;
      a.textContent = cta.label;
      this.messagesEl.appendChild(a);
      this.scrollToBottom();
    }

    // --- #7 — WhatsApp Escalation Card ---

    addEscalationCard() {
      // Build context from last 2 user messages
      const contextMsgs = this.userMessages.slice(-2).join('\n');
      const waLink = `https://wa.me/${this.waNumber}?text=${encodeURIComponent(contextMsgs)}`;

      const card = document.createElement('div');
      card.className = 'chat-escalation';
      card.innerHTML = `
        <div class="chat-escalation__icon">
          <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><path fill="#25D366" d="M16.01 2.667A13.29 13.29 0 002.667 15.94 13.2 13.2 0 004.6 22.68l-1.267 4.653 4.787-1.253A13.33 13.33 0 0016.01 28.6c7.36 0 13.32-5.96 13.32-13.32S23.37 2.667 16.01 2.667zm0 24.36a11.04 11.04 0 01-5.62-1.533l-.4-.24-4.16 1.093 1.113-4.067-.267-.413A10.96 10.96 0 014.96 15.94c0-6.093 4.96-11.04 11.053-11.04S27.04 9.847 27.04 15.94s-4.94 11.087-11.03 11.087zm6.06-8.28c-.333-.167-1.967-.973-2.273-1.08-.307-.113-.527-.167-.753.167s-.86 1.08-1.06 1.307c-.193.22-.393.247-.727.08-.333-.167-1.407-.52-2.68-1.653-.993-.88-1.66-1.973-1.853-2.307-.193-.333-.02-.513.147-.68.147-.147.333-.387.5-.58.167-.193.22-.333.333-.553.113-.227.06-.42-.027-.587-.087-.167-.753-1.813-1.033-2.48-.273-.653-.547-.567-.753-.573l-.64-.013c-.22 0-.58.08-.887.413-.307.333-1.167 1.14-1.167 2.78s1.193 3.227 1.36 3.447c.167.22 2.347 3.587 5.687 5.027.793.347 1.413.547 1.9.7.793.253 1.52.22 2.093.133.64-.1 1.967-.807 2.247-1.58.273-.78.273-1.447.193-1.587-.08-.14-.3-.22-.633-.387z"/></svg>
        </div>
        <div class="chat-escalation__body">
          <p class="chat-escalation__text">${this.escapeHtml(this.strings.escalationPrompt)}</p>
          <a class="chat-escalation__btn" href="${waLink}" target="_blank" rel="noopener">
            <svg viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 01-4.29-1.234l-.29-.174-3.176.834.847-3.104-.192-.3A7.96 7.96 0 014 12a8 8 0 1116 0 8 8 0 01-8 8z"/></svg>
            ${this.escapeHtml(this.strings.escalationButton)}
          </a>
        </div>
      `;

      this.messagesEl.appendChild(card);
      this.scrollToBottom();
    }

    // --- Catalog drill-down (client-side, no AI) ---

    showCatalogLevel(items, level, path) {
      if (!items || items.length === 0) return;

      if (level === 1) {
        this.showCatalogGrid(items, path);
      } else if (level === 2) {
        this.showCatalogChips(items, path);
      } else {
        this.showCatalogCarousel(items, path);
      }
    }

    /*
     * Card art (assets/catcard-<handle>.jpg) is headline-free, like the categories page and the
     * collection banner: the menu title is drawn over it, with the parent's title as a small line
     * above on level 2. Product is on the right of the photo, so the title stays on the left in
     * both languages. Returns '' when the item has no usable art.
     */
    catalogArtHtml(item, parentTitle) {
      if (!item.art || !this.isSafeUrl(item.art)) return '';
      const parent = parentTitle
        ? `<span class="chat-carousel__art-parent">${this.escapeHtml(parentTitle)}</span>`
        : '';
      return `<span class="chat-carousel__art">`
        + `<img class="chat-carousel__art-img" src="${this.escapeHtml(item.art)}" alt="" loading="lazy" onerror="this.style.display='none'" />`
        + `<span class="chat-carousel__art-text">${parent}`
        + `<span class="chat-carousel__art-title">${this.escapeHtml(item.title)}</span></span>`
        + `</span>`;
    }

    /* L1: Horizontal carousel (WhatsApp-style swipeable cards, 1.91:1 banners) */
    showCatalogGrid(items, path) {
      const scroll = document.createElement('div');
      scroll.className = 'chat-carousel chat-carousel--l1';

      const track = document.createElement('div');
      track.className = 'chat-carousel__track';

      items.forEach((item, index) => {
        const btn = document.createElement('button');
        btn.className = 'chat-carousel__card';
        btn.style.animationDelay = `${index * 0.05}s`;

        const bannerSrc = this.isArabic ? (item.banner_ar || item.banner) : item.banner;
        const imgSrc = item.image;
        let html = this.catalogArtHtml(item, '');

        if (html) {
          btn.setAttribute('aria-label', item.title);
        } else if (bannerSrc && this.isSafeUrl(bannerSrc)) {
          html += `<img class="chat-carousel__img chat-carousel__img--l1" src="${this.escapeHtml(bannerSrc)}" alt="${this.escapeHtml(item.title)}" loading="lazy" onerror="this.style.display='none'" />`;
        } else if (imgSrc) {
          html += `<img class="chat-carousel__img chat-carousel__img--icon" src="${this.escapeHtml(imgSrc)}" alt="" loading="lazy" onerror="this.style.display='none'" />`;
          html += `<span class="chat-carousel__title">${this.escapeHtml(item.title)}</span>`;
        }
        btn.innerHTML = html;

        btn.addEventListener('click', () => {
          track.querySelectorAll('.chat-carousel__card').forEach(c => c.disabled = true);
          this.handleCatalogClick(index, 1, path);
        });

        track.appendChild(btn);
      });

      scroll.appendChild(track);
      this.addCarouselArrows(scroll, track);
      this.messagesEl.appendChild(scroll);
      this.scrollToBottom();
    }

    /* L2: Icon card carousel (reuses .chat-carousel structure, supports wide banners) */
    showCatalogChips(items, path) {
      const hasBanners = items.some(i => i.art || (this.isArabic ? (i.banner_ar || i.banner) : i.banner));
      const parentTitle = this.catalog[path[0]]?.title || '';
      const scroll = document.createElement('div');
      scroll.className = hasBanners ? 'chat-carousel chat-carousel--l2-banner' : 'chat-carousel chat-carousel--l2';

      const track = document.createElement('div');
      track.className = 'chat-carousel__track';

      items.forEach((item, index) => {
        const btn = document.createElement('button');
        btn.className = 'chat-carousel__card';
        btn.style.animationDelay = `${index * 0.05}s`;

        const bannerSrc = this.isArabic ? (item.banner_ar || item.banner) : item.banner;
        const imgSrc = item.icon;
        let html = this.catalogArtHtml(item, parentTitle);

        if (html) {
          btn.setAttribute('aria-label', parentTitle ? `${parentTitle}, ${item.title}` : item.title);
        } else if (bannerSrc && this.isSafeUrl(bannerSrc)) {
          html += `<img class="chat-carousel__img chat-carousel__img--banner" src="${this.escapeHtml(bannerSrc)}" alt="${this.escapeHtml(item.title)}" loading="lazy" onerror="this.style.display='none'" />`;
        } else if (imgSrc) {
          html += `<img class="chat-carousel__img chat-carousel__img--icon" src="${this.escapeHtml(imgSrc)}" alt="" loading="lazy" onerror="this.style.display='none'" />`;
          html += `<span class="chat-carousel__title">${this.escapeHtml(item.title)}</span>`;
        }
        btn.innerHTML = html;

        btn.addEventListener('click', () => {
          track.querySelectorAll('.chat-carousel__card').forEach(c => c.disabled = true);
          this.handleCatalogClick(index, 2, path);
        });

        track.appendChild(btn);
      });

      scroll.appendChild(track);
      this.addCarouselArrows(scroll, track);
      this.messagesEl.appendChild(scroll);
      this.scrollToBottom();
    }

    /* L3: Compact horizontal carousel */
    showCatalogCarousel(items, path) {
      const scroll = document.createElement('div');
      scroll.className = 'chat-carousel chat-carousel--compact';

      const track = document.createElement('div');
      track.className = 'chat-carousel__track';

      items.forEach((item, index) => {
        const el = document.createElement('a');
        el.className = 'chat-carousel__card';
        el.style.animationDelay = `${index * 0.05}s`;
        el.href = (item.url && item.url.includes('/collections/'))
          ? item.url
          : `${this.langPrefix}/collections/${this.escapeHtml(item.handle)}`;

        const imgSrc = item.image;
        let html = '';
        if (imgSrc) {
          html += `<img class="chat-carousel__img" src="${this.escapeHtml(imgSrc)}" alt="" loading="lazy" onerror="this.outerHTML='<div class=\\'chat-carousel__img--empty\\'></div>'" />`;
        } else {
          html += '<div class="chat-carousel__img--empty"></div>';
        }
        html += `<span class="chat-carousel__title">${this.escapeHtml(item.title)}</span>`;
        el.innerHTML = html;

        track.appendChild(el);
      });

      scroll.appendChild(track);
      this.addCarouselArrows(scroll, track);
      this.messagesEl.appendChild(scroll);
      // Auto-shrink long single-word titles to prevent ugly character breaks
      requestAnimationFrame(() => {
        scroll.querySelectorAll('.chat-carousel__title').forEach((el) => {
          if (el.textContent.trim().includes(' ')) return;
          const minFont = 7;
          let size = parseFloat(getComputedStyle(el).fontSize);
          el.style.whiteSpace = 'nowrap';
          el.style.display = 'block';
          el.style.webkitLineClamp = 'unset';
          while (el.scrollWidth > el.clientWidth && size > minFont) {
            size -= 0.5;
            el.style.fontSize = size + 'px';
          }
          el.style.whiteSpace = '';
          el.style.display = '';
          el.style.webkitLineClamp = '';
        });
      });
      this.scrollToBottom();
    }

    /* Desktop arrows for carousels */
    addCarouselArrows(wrapper, track) {
      const chevron = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>';

      const leftBtn = document.createElement('button');
      leftBtn.className = 'chat-carousel__arrow chat-carousel__arrow--left';
      leftBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>`;

      const rightBtn = document.createElement('button');
      rightBtn.className = 'chat-carousel__arrow chat-carousel__arrow--right';
      rightBtn.innerHTML = chevron;

      const isRTL = this.isArabic;

      const updateArrows = () => {
        if (isRTL) {
          /* RTL: scrollLeft is 0 at start (right edge) and goes negative.
             CSS swaps positions: --left arrow is on RIGHT, --right arrow is on LEFT.
             Left arrow (→ on right) = "back to start", right arrow (← on left) = "see more". */
          leftBtn.disabled = Math.abs(track.scrollLeft) <= 5;
          rightBtn.disabled = Math.abs(track.scrollLeft) + track.clientWidth >= track.scrollWidth - 5;
        } else {
          leftBtn.disabled = track.scrollLeft <= 5;
          rightBtn.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 5;
        }
      };

      const scrollAmount = () => {
        const card = track.querySelector('.chat-carousel__card');
        return card ? card.offsetWidth + 10 : 100;
      };

      leftBtn.addEventListener('click', () => {
        track.scrollBy({ left: isRTL ? scrollAmount() : -scrollAmount(), behavior: 'smooth' });
      });
      rightBtn.addEventListener('click', () => {
        track.scrollBy({ left: isRTL ? -scrollAmount() : scrollAmount(), behavior: 'smooth' });
      });

      track.addEventListener('scroll', updateArrows, { passive: true });
      setTimeout(updateArrows, 400);

      wrapper.appendChild(leftBtn);
      wrapper.appendChild(rightBtn);
    }

    handleCatalogClick(index, level, path) {
      const newPath = [...path, index];

      if (level === 1) {
        const item = this.catalog[index];
        if (!item) return;
        this.addUserMessage(item.title);

        if (item.children && item.children.length > 0) {
          this.addAssistantMessage(this.strings.chooseSection);
          this.showCatalogLevel(item.children, 2, newPath);
        } else {
          this.searchFromCatalog(newPath);
        }
      } else if (level === 2) {
        const l1 = this.catalog[path[0]];
        const item = l1?.children?.[index];
        if (!item) return;
        this.addUserMessage(item.title);

        if (item.children && item.children.length > 0) {
          this.addAssistantMessage(this.strings.chooseType);
          this.showCatalogLevel(item.children, 3, newPath);
        } else {
          this.searchFromCatalog(newPath);
        }
      }
    }

    async searchFromCatalog(path) {
      const l1 = this.catalog[path[0]];
      const l2 = l1?.children?.[path[1]];
      const item = l2 || l1;
      if (!item) return;

      // Extract collection handle from URL (/collections/{handle}) or use item.handle
      let handle = item.handle;
      if (item.url) {
        const m = item.url.match(/\/collections\/([^/?#]+)/);
        if (m) handle = m[1];
      }

      if (handle) {
        await this.fetchCollectionProducts(handle);
      }
    }

    async fetchCollectionProducts(handle) {
      this.showTyping();
      const languageCode = this.isArabic ? 'AR' : 'EN';

      try {
        const query = `
          query($handle: String!, $language: LanguageCode!) @inContext(language: $language) {
            collectionByHandle(handle: $handle) {
              title
              handle
              image { url altText }
              products(first: 8) {
                edges {
                  node {
                    id
                    title
                    handle
                    vendor
                    availableForSale
                    featuredImage { url altText }
                    priceRange { minVariantPrice { amount currencyCode } }
                    compareAtPriceRange { minVariantPrice { amount } }
                    options {
                      name
                      optionValues {
                        name
                        swatch { color }
                      }
                    }
                    variants(first: 100) {
                      edges {
                        node {
                          id
                          availableForSale
                          selectedOptions { name value }
                          price { amount currencyCode }
                          compareAtPrice { amount }
                          image { url }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `;

        const res = await fetch(`${this.shopUrl}/api/2024-10/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken
          },
          body: JSON.stringify({
            query,
            variables: { handle, language: languageCode }
          })
        });

        this.hideTyping();

        if (!res.ok) {
          this.addAssistantMessage(this.strings.noProducts);
          return;
        }

        const data = await res.json();
        const collection = data?.data?.collectionByHandle;

        if (!collection) {
          this.addAssistantMessage(this.strings.noProducts);
          return;
        }

        // Show collection card
        this.addCollectionCards([collection]);

        const products = collection.products?.edges?.map(e => e.node) || [];
        if (products.length === 0) {
          this.addAssistantMessage(this.strings.noProducts);
          return;
        }

        this.addProductCards(products);
        this.addViewAllLink(collection);
        this.addFollowUpChips();
      } catch {
        this.hideTyping();
        this.addAssistantMessage(this.strings.noProducts);
      }
    }

    addProductCards(products, prefs) {
      if (!products || products.length === 0) return;

      const scroll = document.createElement('div');
      scroll.className = 'chat-carousel chat-carousel--products';

      const track = document.createElement('div');
      track.className = 'chat-carousel__track';

      products.forEach((product) => {
        const card = this.createProductCard(product, prefs);
        track.appendChild(card);
      });

      scroll.appendChild(track);
      this.addCarouselArrows(scroll, track);
      this.messagesEl.appendChild(scroll);
      this.scrollToBottom();
    }

    createProductCard(product, prefs) {
      const a = document.createElement('a');
      a.className = 'chat-product-card';
      a.href = `${this.langPrefix}/products/${this.escapeHtml(product.handle)}`;

      // #7 — Reset frustration counter on product card click
      a.addEventListener('click', () => {
        this.msgsSinceInteraction = 0;
        if (prefs?.bsSources?.[product.id]) {
          this.bsTrack({ eventType: 'CLICK', productId: product.id, source: prefs.bsSources[product.id] });
        }
      });

      const image = product.featuredImage;
      const price = product.priceRange?.minVariantPrice;
      const comparePrice = product.compareAtPriceRange?.minVariantPrice;
      const hasDiscount = comparePrice && parseFloat(comparePrice.amount) > parseFloat(price.amount);

      // Variant data processing
      const variantData = this.processVariantData(product, prefs);
      const firstVariant = variantData
        ? this.findVariant(variantData.variants, variantData.selections) || variantData.variants[0]
        : product.variants?.edges?.[0]?.node;
      const canAddToCart = firstVariant?.availableForSale && product.availableForSale;

      let html = '';

      if (image) {
        html += `<img class="chat-product-card__image" src="${this.escapeHtml(image.url)}&width=300" alt="${this.escapeHtml(image.altText || product.title)}" loading="lazy" />`;
      } else {
        html += '<div class="chat-product-card__image" style="display:flex;align-items:center;justify-content:center;background:#f5f5f5"><span style="color:#bbb;font-size:11px">No image</span></div>';
      }

      if (hasDiscount) {
        const discountPct = Math.round((1 - parseFloat(price.amount) / parseFloat(comparePrice.amount)) * 100);
        if (discountPct > 0) {
          html += `<span class="chat-product-card__badge">-${discountPct}%</span>`;
        }
      }

      html += '<div class="chat-product-card__info">';
      if (product.vendor) {
        html += `<p class="chat-product-card__vendor">${this.escapeHtml(product.vendor)}</p>`;
      }
      html += `<p class="chat-product-card__title">${this.escapeHtml(product.title)}</p>`;

      if (price) {
        html += `<div class="chat-product-card__price">${this.formatPrice(price.amount)}`;
        if (hasDiscount) {
          html += `<span class="chat-product-card__compare-price">${this.formatPrice(comparePrice.amount)}</span>`;
        }
        html += '</div>';
      }

      if (!product.availableForSale) {
        html += `<p class="chat-product-card__sold-out">${this.escapeHtml(this.strings.soldOut)}</p>`;
      }

      html += '</div>';

      // Variant picker chips
      if (variantData) {
        html += '<div class="chat-variant-picker">';
        variantData.options.forEach(opt => {
          const values = opt.optionValues;
          if (values.length <= 1) return;

          html += `<div class="chat-variant-row" data-option="${this.escapeHtml(opt.name)}">`;

          if (opt.type === 'color') {
            const maxShow = 6;
            const shown = values.slice(0, maxShow);
            const overflow = values.length - maxShow;

            shown.forEach(v => {
              const hex = v.swatch?.color || this.cssColorFallback(v.name);
              const isSelected = variantData.selections[opt.name] === v.name;
              const light = hex ? this.isLightColor(hex) : false;
              let cls = 'chat-variant-chip chat-variant-chip--color';
              if (isSelected) cls += ' chat-variant-chip--selected';
              if (light) cls += ' chat-variant-chip--light';

              if (hex) {
                html += `<button class="${cls}" data-value="${this.escapeHtml(v.name)}" style="background-color:${hex}" title="${this.escapeHtml(v.name)}"></button>`;
              } else {
                // No color data — show as text pill
                html += `<button class="chat-variant-chip chat-variant-chip--size${isSelected ? ' chat-variant-chip--selected' : ''}" data-value="${this.escapeHtml(v.name)}">${this.escapeHtml(v.name)}</button>`;
              }
            });

            if (overflow > 0) {
              html += `<span class="chat-variant-overflow">+${overflow}</span>`;
            }
          } else {
            values.forEach(v => {
              const isSelected = variantData.selections[opt.name] === v.name;
              let cls = 'chat-variant-chip chat-variant-chip--size';
              if (isSelected) cls += ' chat-variant-chip--selected';
              html += `<button class="${cls}" data-value="${this.escapeHtml(v.name)}">${this.escapeHtml(v.name)}</button>`;
            });
          }

          html += '</div>';
        });
        html += '</div>';
      }

      // Button bar: View Item + ATC
      html += '<div class="chat-product-card__actions">';
      html += `<span class="chat-product-card__view">${this.escapeHtml(this.strings.viewItem)}</span>`;
      if (canAddToCart) {
        html += `<button class="chat-product-card__atc" data-variant-gid="${this.escapeHtml(firstVariant.id)}" aria-label="${this.escapeHtml(this.strings.addedToCart)}">
          <svg viewBox="0 0 24 24"><path d="M11 9h2V6h3V4h-3V1h-2v3H8v2h3v3zm-4 9c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2zm-9.83-3.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.86-7.01L19.42 4h-.01l-1.1 2-2.76 5H8.53l-.13-.27L6.16 6l-.95-2-.94-2H1v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25z"/></svg>
        </button>`;
      }
      html += '</div>';

      a.innerHTML = html;

      // Bind variant chip clicks (prevent navigation, stop propagation)
      if (variantData) {
        this.updateChipAvailability(a, variantData);
        a.querySelectorAll('.chat-variant-chip').forEach(chip => {
          chip.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.handleVariantChipClick(a, chip, variantData);
          });
        });
      }

      // Bind ATC click (prevent navigation)
      if (canAddToCart) {
        const atcBtn = a.querySelector('.chat-product-card__atc');
        if (atcBtn) {
          atcBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.addToCart(atcBtn.dataset.variantGid, atcBtn, product, prefs);
          });
        }
      }

      return a;
    }

    // --- #11 — Add to Cart ---

    async addToCart(variantGid, btn, product, prefs) {
      const numericId = variantGid.split('/').pop();

      try {
        const res = await fetch(`${window.theme?.routes?.cartAdd || '/cart/add'}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{ id: parseInt(numericId, 10), quantity: 1 }]
          })
        });

        if (!res.ok) throw new Error('ATC failed');

        // Update cart icon count
        const cartRes = await fetch(`${window.theme?.routes?.cart || '/cart'}.js`);
        const cartData = await cartRes.json();
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          if (cartData.item_count > 0) {
            cartIconBubble.innerHTML = '<div class="header__cart-count absolute bg-theme-text text-theme-bg text-center"><span aria-hidden="true" class="header__cart-count-number">' + cartData.item_count + '</span></div>';
          } else {
            cartIconBubble.innerHTML = '';
          }
        }

        // Refresh cart drawer contents
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));

        // Dispatch cart add event
        this.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true }));

        if (product && prefs?.bsSources?.[product.id]) {
          this.bsTrack({ eventType: 'ACCEPTED', productId: product.id, source: prefs.bsSources[product.id] });
        }
        if (product) this.showPairs(product).catch(() => {});

        // Show checkmark success state
        btn.classList.add('chat-product-card__atc--success');
        btn.innerHTML = '<svg viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>';
        setTimeout(() => {
          btn.classList.remove('chat-product-card__atc--success');
          btn.innerHTML = '<svg viewBox="0 0 24 24"><path d="M11 9h2V6h3V4h-3V1h-2v3H8v2h3v3zm-4 9c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2zm-9.83-3.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.86-7.01L19.42 4h-.01l-1.1 2-2.76 5H8.53l-.13-.27L6.16 6l-.95-2-.94-2H1v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25z"/></svg>';
        }, 2000);
      } catch (err) {
        // Shake animation on error
        btn.classList.add('chat-product-card__atc--error');
        setTimeout(() => btn.classList.remove('chat-product-card__atc--error'), 400);
      }
    }

    showTyping() {
      const el = document.createElement('div');
      el.className = 'chat-typing';
      el.id = 'chat-typing';
      el.innerHTML = '<span></span><span></span><span></span>';
      this.messagesEl.appendChild(el);
      this.scrollToBottom();
    }

    hideTyping() {
      const el = this.messagesEl.querySelector('#chat-typing');
      if (el) el.remove();
    }

    scrollToBottom() {
      requestAnimationFrame(() => {
        this.messagesEl.scrollTo({ top: this.messagesEl.scrollHeight, behavior: 'smooth' });
      });
    }

    // --- Core send flow ---

    async handleSend(text) {
      const message = text || this.input.value.trim();
      if (!message || this.isProcessing) return;

      this.input.value = '';
      this.turn++;
      this.addUserMessage(message);
      this.userMessages.push(message);
      await this.processMessage(message);
    }

    handleQuickReply(text, label) {
      this.turn++;
      this.addUserMessage(label || text);
      this.userMessages.push(text);
      // #7 — Reset frustration counter on chip click
      this.msgsSinceInteraction = 0;
      this.processMessage(text);
    }

    async processMessage(text) {
      if (!navigator.onLine) {
        this.addAssistantMessage(this.strings.offline);
        return;
      }

      // --- Client-side: order tracking (no AI needed) ---
      // The Arabic prompt shows the prefix in Arabic-Indic digits, so shoppers
      // may type the order number (or phone) that way; match on ASCII digits.
      const latinDigits = (s) => s
        .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
        .replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06F0));

      // Step 2: we're waiting for email/phone after an order number
      if (this.pendingOrderNumber) {
        const credential = latinDigits(text.trim());
        const orderNum = this.pendingOrderNumber;
        this.pendingOrderNumber = null;
        await this.trackAndShowOrder(orderNum, credential);
        return;
      }

      // Step 1: detect order number pattern (5000 + 4 digits, 8 in total)
      const orderMatch = latinDigits(text).match(/\b(5000\d{4})\b/);
      if (orderMatch) {
        this.pendingOrderNumber = orderMatch[1];
        this.addAssistantMessage(this.strings.credentialPrompt);
        return;
      }

      // --- "Picked for you" / "Best sellers" — better-sell (no AI needed) ---
      if (this.betterSell && text === this.strings.chipPicks) {
        this.isProcessing = true;
        this.sendBtn.disabled = true;
        try {
          await this.showPicks();
        } finally {
          this.isProcessing = false;
          this.sendBtn.disabled = false;
        }
        return;
      }

      // --- #8 — Client-side intent matching (no AI needed) ---
      const localIntent = this.matchLocalIntent(text);
      if (localIntent) {
        this.addAssistantMessage(localIntent.message);
        this.bsLogTurns([
          { role: 'user', content: text, route: 'theme-intent' },
          { role: 'assistant', content: localIntent.message, route: 'theme-intent' }
        ]);
        if (localIntent.cta) {
          this.addCtaButton(localIntent.cta);
        }
        if (localIntent.showCatalog) {
          this.showCatalogLevel(this.catalog, 1, []);
        }
        if (localIntent.showEscalation) {
          this.addEscalationCard();
        }
        if (localIntent.quickReplies && localIntent.quickReplies.length > 0) {
          this.addQuickReplies(localIntent.quickReplies);
        }
        return;
      }

      // --- "Show more" / "Under <budget>" chip — re-run last search (no AI needed) ---
      const isShowMore = text === this.strings.chipMore;
      const isCheaper = text === this.strings.chipCheaper;
      if (isShowMore || isCheaper) {
        if (this.lastSearchTerms) {
          this.isProcessing = true;
          this.sendBtn.disabled = true;
          this.addAssistantMessage(this.strings.showMoreMessage);
          const budget = isCheaper ? this.budgetAmount : null;
          await this.searchAndShowProducts(this.lastSearchTerms, this.lastSearchFilters || {}, budget);
          this.addFollowUpChips();
          this.isProcessing = false;
          this.sendBtn.disabled = false;
        } else {
          this.addAssistantMessage(this.strings.noPreviousSearch);
          this.showCatalogLevel(this.catalog, 1, []);
        }
        return;
      }

      // --- Synonym lookup (instant, no AI needed) ---
      // Questions and support requests skip the product shortcuts: the API
      // (Jev intent routing, then Claude) answers them.
      const isQuestion = this.looksLikeQuestion(text);

      const synonymTerms = isQuestion ? null : this.lookupSynonyms(text);
      if (synonymTerms) {
        this.isProcessing = true;
        this.sendBtn.disabled = true;
        this.lastSearchTerms = synonymTerms;
        this.lastSearchFilters = {};
        const synQuery = this.parseQuery(text);
        if (synQuery.hasProduct) this.lastQuery = synQuery;
        await this.searchAndShowProducts(synonymTerms, {}, null);
        this.bsLogTurns([{ role: 'user', content: text, route: 'theme-synonyms' }]);
        // #2 — Follow-up chips after product grid
        this.addFollowUpChips();
        this.isProcessing = false;
        this.sendBtn.disabled = false;
        return;
      }

      // --- Direct search: try Storefront API before AI ---
      this.isProcessing = true;
      this.sendBtn.disabled = true;
      this.showTyping();

      const directFound = isQuestion ? false : await this.tryDirectSearch(text);
      if (directFound) {
        this.bsLogTurns([{ role: 'user', content: text, route: 'theme-search' }]);
        this.isProcessing = false;
        this.sendBtn.disabled = false;
        return;
      }

      // --- AI-powered fallback via Vercel proxy ---

      // #7 — Increment frustration counter
      this.msgsSinceInteraction++;

      // Add to conversation history
      this.conversationHistory.push({ role: 'user', content: text });
      if (this.conversationHistory.length > 6) {
        this.conversationHistory = this.conversationHistory.slice(-6);
      }

      try {
        const response = await this.callAI(this.conversationHistory);
        this.hideTyping();

        if (!response) {
          this.addAssistantMessage(this.strings.error);
          return;
        }

        // Add assistant response to history
        this.conversationHistory.push({ role: 'assistant', content: response.message || '' });
        this.bsLogTurns([
          { role: 'user', content: text, route: response.intent || 'claude', intent: response.intent },
          {
            role: 'assistant',
            content: response.message || '',
            route: response.intent || 'claude',
            intent: response.intent,
            products: response.cta && response.cta.url ? [response.cta.url] : []
          }
        ].filter((t) => t.content));

        // #3 — Typing indicator delay (700ms) for natural "composing" feel
        this.showTyping();
        await new Promise((resolve) => setTimeout(resolve, 700));
        this.hideTyping();

        // Render message
        if (response.message) {
          this.addAssistantMessage(response.message);
        }

        // Search for products if search_terms provided
        let hadProducts = false;
        if (response.search_terms && response.search_terms.length > 0) {
          // Enrich AI search_terms with synonym lookups
          let enrichedTerms = [...response.search_terms];
          response.search_terms.forEach((term) => {
            const syns = this.lookupSynonyms(term);
            if (syns) {
              syns.forEach((s) => {
                if (!enrichedTerms.includes(s)) enrichedTerms.push(s);
              });
            }
          });
          this.lastSearchTerms = enrichedTerms;
          this.lastSearchFilters = response.filters || {};
          const aiQuery = this.parseQuery(text);
          if (aiQuery.hasProduct) this.lastQuery = aiQuery;
          await this.searchAndShowProducts(enrichedTerms, response.filters, response.budget);
          hadProducts = true;
        }

        // #9 — CTA from the AI response: a section of the store's own menu (Jev's
        // pick) is shown as a card with its art; any other link stays a button.
        if (response.cta) {
          const card = this.catalogCardForUrl(response.cta.url, response.cta.label);
          if (card && !this.messagesEl.querySelector(`.chat-collection-card[href$="/collections/${card.handle}"]`)) {
            this.addCollectionCards([card]);
          } else if (!card) {
            this.addCtaButton(response.cta);
          }
        }

        // #9 — Auto-escalation for support intent (Jev's frustration route lands here too)
        let escalated = false;
        if (response.intent === 'support' && response.message && !response.message.includes('wa.me')) {
          this.addEscalationCard();
          escalated = true;
          this.msgsSinceInteraction = 0;
        }

        // #5 — Never-dead-end: ensure quick replies are always shown
        if (response.quick_replies && response.quick_replies.length > 0) {
          this.addQuickReplies(response.quick_replies);
        } else if (hadProducts) {
          // #2 — Follow-up chips after product grid
          this.addFollowUpChips();
        } else {
          // Fallback default chips
          this.addQuickReplies(this.getDefaultChips(false));
        }

        // #7 — WhatsApp escalation after 3 unanswered messages (once per turn)
        if (this.msgsSinceInteraction >= 3 && !escalated) {
          this.addEscalationCard();
          this.msgsSinceInteraction = 0;
        }
      } catch (err) {
        this.hideTyping();
        if (err.status === 429) {
          this.addAssistantMessage(this.strings.rateLimited);
        } else {
          this.addAssistantMessage(this.strings.error);
        }
        // #5 — Even on error, show chips
        this.addQuickReplies(this.getDefaultChips(false));
      } finally {
        this.isProcessing = false;
        this.sendBtn.disabled = false;
      }
    }

    // --- #8 — Client-side intent matching ---

    matchLocalIntent(text) {
      const t = text.trim();
      // Egyptian customers write "فين طلبي؟": the question mark is not part of the intent.
      const q = t.replace(/[\s?؟!.،,]+$/, '');

      // Greeting
      if (/^(hi|hello|hey|مرحبا|هلا|السلام عليكم|هاي|مرحبًا|هلو|سلام عليكم|أهلا|اهلا|أهلاً|اهلاً|أهلا وسهلا|اهلا وسهلا|ازيك|إزيك|ازيكم|إزيكم|ازيك يا باشا|صباح الخير|مساء الخير|عامل ايه|عامل إيه|عامله ايه|عاملة إيه|عاملين ايه|عاملين إيه|اخبارك ايه|أخبارك إيه)$/i.test(q)) {
        return {
          message: this.strings.welcome,
          quickReplies: [this.strings.chipTrack, this.strings.chipDeals, this.strings.chipHelp, this.strings.chipSupportLabel],
          showCatalog: true
        };
      }

      // Thanks
      if (/^(thanks|thank you|شكرا|مشكور|ثانكيو|شكرًا|ممنون|متشكر|متشكرة|متشكره|متشكرين|تسلم|تسلمي|تسلموا|ألف شكر|الف شكر|شكرا ليك|شكراً ليك|شكرا لك|شكراً لك|شكرا جزيلا|شكراً جزيلاً|كتر خيرك|ربنا يخليك|ميرسي|ميرسى)$/i.test(q)) {
        return {
          message: this.strings.youreWelcome,
          quickReplies: [this.strings.chipDeals, this.strings.chipHelp, this.strings.chipSupportLabel],
          showCatalog: false
        };
      }

      // Goodbye
      if (/^(bye|goodbye|مع السلامة|مع السلامه|باي|باي باي|وداعا|في أمان الله|سلام|أشوفك|اشوفك|تصبح على خير|تصبحوا على خير)$/i.test(q)) {
        return {
          message: this.strings.goodbye,
          quickReplies: [],
          showCatalog: false
        };
      }

      // Talk to support
      if (t === this.strings.chipSupportLabel || /^(talk to support|support|الدعم|تحدث مع الدعم|تواصل مع الدعم|خدمة العملاء|كلمني|كلموني|اكلم حد|أكلم حد|عايز اكلم حد|عاوز اكلم حد|عايز أكلم حد|عاوز أكلم حد|عايزة اكلم حد|عاوزة اكلم حد|عايزة أكلم حد|عاوزة أكلم حد|ممكن اكلم حد|ممكن أكلم حد|اتكلم مع حد|أتكلم مع حد|عايز اتكلم مع حد|عاوز اتكلم مع حد|عايز أتكلم مع حد|عاوز أتكلم مع حد|حد يكلمني|محتاج اكلم حد|محتاج أكلم حد|محتاجة اكلم حد|محتاجة أكلم حد)$/i.test(q)) {
        return {
          message: this.strings.supportMessage,
          showEscalation: true,
          quickReplies: [this.strings.chipTrack, this.strings.chipHelp, this.strings.chipDeals],
          showCatalog: false
        };
      }

      // Track my order
      if (t === this.strings.chipTrack || /^(track.*(order|shipment)|تتبع.*طلب|تتبع الطلب|رقم طلبي|تتبع الاوردر|تتبع الأوردر|تتبع اوردري|تتبع أوردري|اتابع طلبي|أتابع طلبي|اتابع الاوردر|أتابع الأوردر|طلبي فين|فين طلبي|الطلب فين|فين الطلب|الطلبية فين|فين الطلبية|الاوردر فين|الأوردر فين|فين الاوردر|فين الأوردر|اوردري فين|أوردري فين|فين اوردري|فين أوردري|الشحنة فين|فين الشحنة|شحنتي فين|فين شحنتي)$/i.test(q)) {
        return {
          message: this.strings.trackPrompt,
          quickReplies: [],
          showCatalog: false
        };
      }

      // Returns / refund / exchange
      if (/^(returns?|refund|exchange|سياسة الإرجاع|ارجاع|إرجاع|استبدال|استرجاع|رجوع|مرتجع|مرتجعات|ارجع|أرجع|عايز ارجع|عاوز ارجع|عايز أرجع|عاوز أرجع|عايزة ارجع|عاوزة ارجع|عايزة أرجع|عاوزة أرجع|عايز استبدل|عاوز استبدل|عايز أستبدل|عاوز أستبدل|عايزة استبدل|عاوزة استبدل|عايزة أستبدل|عاوزة أستبدل|اغير المقاس|أغير المقاس|تغيير المقاس|ارجاع الطلب|إرجاع الطلب|رجوع الطلب|ارجاع الاوردر|إرجاع الأوردر|استرجاع الفلوس|ارجاع الفلوس|إرجاع الفلوس|سياسة الارجاع|سياسة المرتجعات)$/i.test(q)) {
        return {
          message: this.strings.returnsMessage,
          cta: { label: this.strings.returnsUrlLabel, url: this.returnsUrl },
          quickReplies: [this.strings.chipSupportLabel, this.strings.chipHelp, this.strings.chipDeals],
          showCatalog: false
        };
      }

      // Help me choose / browse
      if (t === this.strings.chipHelp || /^(help.*(choose|pick|find)|ساعدني أختار|ساعدني اختار|ساعدني|ساعديني|أبي أختار|عايز اختار|عاوز اختار|عايز أختار|عاوز أختار|عايزة اختار|عاوزة اختار|عايزة أختار|عاوزة أختار|اختارلي|اختار لي|اختارولي|مش عارف اختار|مش عارف أختار|مش عارفة اختار|مش عارفة أختار|مش عارف|مش عارفة|محتار|محتارة|محتاره|اقترح عليا|اقترح علي|اقترحلي|اقترح لي|انصحني|إنصحني|نصحني|ايه رأيك|إيه رأيك)$/i.test(q)) {
        return {
          message: this.strings.helpChooseMessage,
          quickReplies: [],
          showCatalog: true
        };
      }

      // Browse catalog / shop
      if (/^(browse|shop|catalog|categories|تسوق|تصفح|الأقسام|أقسام|الاقسام|اقسام|وريني الاقسام|وريني الأقسام|ورينى الاقسام|عايز اتفرج|عاوز اتفرج|عايز أتفرج|عاوز أتفرج|عايزة اتفرج|عاوزة اتفرج|عايزة أتفرج|عاوزة أتفرج|اتفرج|أتفرج|بتفرج|عندكم ايه|عندكم إيه|فيه ايه|فيه إيه|ايه الجديد|إيه الجديد)$/i.test(q)) {
        return {
          message: this.strings.browseMessage,
          quickReplies: [],
          showCatalog: true
        };
      }

      // Not-carried brands (no \b — word boundaries don't work with Arabic)
      if (/(nike|new\s*balance|under\s*armour|نايك|نايكي|نيو بالانس|اندر ارمور|اندر آرمور)/i.test(q)) {
        return {
          message: this.strings.notCarried,
          quickReplies: [this.strings.chipHelp, this.strings.chipDeals, this.strings.chipSupportLabel],
          showCatalog: false
        };
      }

      // Store info / branches / locations
      if (/^(store|stores|branch|branches|location|locations|فرع|فروع|محل|محلات|وين فروعكم|أقرب فرع|اقرب فرع|الفروع|المحلات|فين فروعكم|فروعكم فين|الفروع فين|فين الفروع|فين محلاتكم|محلاتكم فين|فين المحلات|المحلات فين|أقرب فرع ليا|اقرب فرع ليا|أقرب فرع لي|اقرب فرع لي|أقرب فرع فين|اقرب فرع فين|فين أقرب فرع|فين اقرب فرع|عندكم فرع فين|عندكم فروع|عندكم محلات|عندكم فروع فين|عندكم محلات فين|عناوين الفروع|عنوان الفرع|عناوين المحلات|مواعيد الفروع|مواعيد العمل|مواعيد الشغل|الفرع بيفتح امتى|الفرع بيفتح إمتى|بتفتحوا امتى|بتفتحوا إمتى|بتقفلوا امتى|بتقفلوا إمتى)$/i.test(q)) {
        return {
          message: this.strings.storeInfo,
          cta: { label: this.strings.storeLocatorLabel, url: this.storeLocatorUrl },
          quickReplies: [this.strings.chipHelp, this.strings.chipDeals, this.strings.chipSupportLabel],
          showCatalog: false
        };
      }

      // Delivery / shipping
      if (/^(delivery|shipping|توصيل|شحن|التوصيل|الشحن|متى يوصل|وقت التوصيل|الدليفري|الديليفري|دليفري|ديليفري|امتى يوصل|إمتى يوصل|هيوصل امتى|هيوصل إمتى|يوصل امتى|يوصل إمتى|الطلب هيوصل امتى|الطلب هيوصل إمتى|الاوردر هيوصل امتى|الأوردر هيوصل إمتى|التوصيل بكام|الشحن بكام|الدليفري بكام|الديليفري بكام|بكام التوصيل|بكام الشحن|بكام الدليفري|مصاريف الشحن|مصاريف التوصيل|مصاريف الدليفري|التوصيل بياخد قد ايه|التوصيل بياخد قد إيه|الشحن بياخد قد ايه|الشحن بياخد قد إيه|بتوصلوا فين|بتوصلوا لفين|بتوصلو فين|بتوصلو لفين|التوصيل فين|بتشحنوا فين|بتشحنو فين|التوصيل لكل مصر|بتوصلوا لكل مصر|بتوصلوا المحافظات|بتوصلو المحافظات|التوصيل للمحافظات|التوصيل مجاني|التوصيل مجانا|الشحن مجاني|الشحن مجانا)$/i.test(q)) {
        return {
          message: this.strings.deliveryMessage,
          quickReplies: [this.strings.chipHelp, this.strings.chipDeals, this.strings.chipTrack],
          showCatalog: false
        };
      }

      return null;
    }

    // --- #2 — Follow-up chips after product results ---

    addFollowUpChips() {
      this.addQuickReplies([
        this.strings.chipMore,
        this.strings.chipDifferent,
        this.strings.chipCheaper,
        this.strings.chipSupportLabel
      ]);
    }

    // --- #5 — Default fallback chips ---

    getDefaultChips(hadProducts) {
      if (hadProducts) {
        return [this.strings.chipMore, this.strings.chipHelp, this.strings.chipSupportLabel];
      }
      return [this.strings.chipHelp, this.strings.chipDeals, this.strings.chipSupportLabel];
    }

    // --- #6 — Proactive FAB Teaser ---

    initProactiveTeaser() {
      if (!this.teaser) return;

      // Check 24h cooldown
      try {
        const dismissed = window.theme?.storageUtil
          ? window.theme.storageUtil.get('chat-teaser-dismissed')
          : localStorage.getItem('chat-teaser-dismissed');
        if (dismissed && (Date.now() - parseInt(dismissed, 10)) < 24 * 60 * 60 * 1000) {
          return;
        }
      } catch (e) {
        // Storage unavailable
      }

      const config = {
        product: { text: this.strings.teaserProduct, delay: 20000 },
        cart: { text: this.strings.teaserCart, delay: 30000 },
        collection: { text: this.strings.teaserCollection, delay: 45000 },
        index: { text: this.strings.teaserHome, delay: 15000 }
      };

      const entry = config[this.pageType];
      if (!entry || !entry.text) return;

      this.teaserTimer = setTimeout(() => {
        if (this.isOpen || this.teaserShown) return;
        this.teaserShown = true;
        this.teaserText.textContent = entry.text;
        this.teaser.hidden = false;
      }, entry.delay);
    }

    applyWebViewInsets() {
      const ua = navigator.userAgent;
      const isAndroid = /android/i.test(ua);
      if (!isAndroid) return;

      // Multi-method Android WebView detection
      const hasClass = document.documentElement.classList.contains('is-android-webview');
      const hasWVClass = document.documentElement.classList.contains('is-webview');
      const uaHasWV = /\bwv\b/i.test(ua) || /\bwebview\b/i.test(ua);

      // navigator.userAgentData.brands (Chrome 90+) — most reliable for Al Nasser app
      let brandsHasWV = false;
      if (navigator.userAgentData) {
        try {
          const brands = navigator.userAgentData.brands || [];
          brandsHasWV = brands.some(b => /webview/i.test(b.brand));
        } catch(e) {}
      }

      const isAndroidWV = hasClass || hasWVClass || uaHasWV || brandsHasWV;

      if (!isAndroidWV) return;

      // Ensure class is set for CSS rules and open() auto-focus skip
      if (!hasClass) document.documentElement.classList.add('is-android-webview');

      let bottomInset = 60; // Standard Material bottom nav height

      // Check for native bridge
      if (window.AlNasserApp && typeof window.AlNasserApp.getBottomInset === 'function') {
        bottomInset = parseInt(window.AlNasserApp.getBottomInset(), 10) || 60;
      }

      // Use padding-bottom so overlay still visually covers full screen,
      // but content (input bar) is pushed above the native tab bar
      this.overlay.style.paddingBottom = bottomInset + 'px';
      this.fab.style.bottom = (24 + bottomInset) + 'px';
      if (this.teaser) this.teaser.style.bottom = (88 + bottomInset) + 'px';

      // Add interactive-widget=resizes-content to viewport meta so the layout
      // viewport shrinks when the Android keyboard opens
      const meta = document.querySelector('meta[name="viewport"]');
      if (meta) {
        const content = meta.getAttribute('content') || '';
        if (!content.includes('interactive-widget')) {
          meta.setAttribute('content', content + ', interactive-widget=resizes-content');
        }
      }
    }

    dismissTeaser() {
      if (!this.teaser) return;
      this.teaser.hidden = true;
      if (this.teaserTimer) {
        clearTimeout(this.teaserTimer);
        this.teaserTimer = null;
      }
      // Store 24h cooldown
      try {
        if (window.theme?.storageUtil) {
          window.theme.storageUtil.set('chat-teaser-dismissed', Date.now().toString());
        } else {
          localStorage.setItem('chat-teaser-dismissed', Date.now().toString());
        }
      } catch (e) {
        // Storage unavailable
      }
    }

    // --- AI via Vercel proxy ---

    async callAI(messages) {
      // Abort a hung request after 20s so the chat never wedges (the caller's
      // finally re-enables input; an AbortError is handled like any failure)
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      let res;
      try {
        res = await fetch('https://kuwait-version6.vercel.app/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // The API builds the prompt itself; it needs the storefront language
          // and, as a hint, the product or collection page the chat is on.
          body: JSON.stringify({
            lang: this.isArabic ? 'ar' : 'en',
            context: this.pageContext,
            messages
          }),
          signal: controller.signal
        });
      } finally {
        clearTimeout(timeout);
      }

      if (!res.ok) {
        const err = new Error('AI API error');
        err.status = res.status;
        throw err;
      }

      const data = await res.json();
      if (data.error) {
        const err = new Error(data.error);
        err.status = res.status;
        throw err;
      }

      const content = data.text?.trim();
      if (!content) return null;

      return this.parseAIResponse(content);
    }

    parseAIResponse(content) {
      try {
        return JSON.parse(content);
      } catch {
        const match = content.match(/\{[\s\S]*\}/);
        if (match) {
          try {
            return JSON.parse(match[0]);
          } catch {
            // Give up on JSON
          }
        }
        return { message: content, search_terms: [], quick_replies: [] };
      }
    }

    // --- chat transcript (better-sell app proxy) ----------------------------------
    // One best-effort POST per answered turn. The signed proxy adds the customer
    // id server-side, so nothing about identity is sent from here; the API keeps
    // transcripts 90 days (better-sell services/chat-log.server.ts).
    chatSessionId() {
      if (this._chatSession) return this._chatSession;
      let id = null;
      try { id = sessionStorage.getItem('chat-session-id'); } catch { /* storage off */ }
      if (!id) {
        id = (window.crypto && crypto.randomUUID)
          ? crypto.randomUUID()
          : Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
        try { sessionStorage.setItem('chat-session-id', id); } catch { /* storage off */ }
      }
      this._chatSession = id;
      return id;
    }

    bsLogTurns(turns) {
      if (!this.betterSell || !turns || !turns.length) return;
      try {
        fetch(`${this.betterSell}/api/chat-log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: this.chatSessionId(),
            lang: this.isArabic ? 'ar' : 'en',
            page: location.pathname,
            turns: turns.map((t) => ({ ...t, content: String(t.content || '').slice(0, 2000) }))
          }),
          keepalive: true
        }).catch(() => {});
      } catch {
        /* logging never breaks the chat */
      }
    }

    // --- better-sell (app proxy; every call is skipped or ignored without the app) ---
    // Every call is best-effort: a slow or failed answer leaves the chat exactly
    // as it would be without better-sell.
    async bsFetch(path, timeoutMs = 2500) {
      if (!this.betterSell) return null;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const res = await fetch(`${this.betterSell}${path}`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
        return res.ok ? await res.json() : null;
      } catch {
        return null;
      } finally {
        clearTimeout(timer);
      }
    }
    bsTrack(body) {
      if (!this.betterSell) return;
      try {
        fetch(`${this.betterSell}/api/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ surface: 'chat', ...body }),
          keepalive: true
        }).catch(() => {});
      } catch {
        /* tracking never breaks the chat */
      }
    }

    // Search results, best converters first (global conversion score; products
    // without enough views keep their search order at the tail).
    async rankProducts(products) {
      if (!this.betterSell || !products || products.length < 2) return products;
      const byId = new Map(products.map((p) => [String(p.id).split('/').pop(), p]));
      const data = await this.bsFetch(`/api/collection-rank?ids=${[...byId.keys()].join(',')}`, 800);
      const order = Array.isArray(data?.order) ? data.order : null;
      if (!order) return products;
      const ranked = order.map((id) => byId.get(String(id))).filter(Boolean);
      const seen = new Set(ranked);
      return ranked.concat(products.filter((p) => !seen.has(p)));
    }

    // Logged-in customers: who they usually shop for, as shares. Only used to
    // put the likeliest "For whom?" chip first; it never filters anything.
    async loadAudience() {
      if (!this.betterSell || !this.isLoggedIn || this.audience) return;
      const data = await this.bsFetch('/api/chat-audience');
      if (data?.known && Array.isArray(data.audiences)) {
        this.audience = new Map(data.audiences.map((a) => [a.gender, Number(a.share) || 0]));
      }
    }

    orderByAudience(opts) {
      if (!this.audience) return opts;
      // stable: equal shares keep the default Boys, Girls, Men, Women order
      return opts
        .map((o, i) => ({ o, i, share: this.audience.get(o[2]) || 0 }))
        .sort((a, b) => b.share - a.share || a.i - b.i)
        .map(({ o }) => o);
    }

    // better-sell products -> Storefront cards, remembering which engine picked
    // each one so clicks and adds are attributed to it.
    async bsProductsToCards(items) {
      const list = (items || []).filter((p) => p && p.handle && p.productId);
      if (!list.length) return { products: [], sources: {} };
      const products = await this.fetchProductsByHandles(list.map((p) => p.handle));
      const sourceByHandle = new Map(list.map((p) => [p.handle, p.source || 'popularity']));
      const sources = {};
      products.forEach((p) => { sources[p.id] = sourceByHandle.get(p.handle); });
      return { products, sources };
    }

    // "Picked for you" (logged in, with history) or "Best sellers", following the
    // page: that collection's own best sellers (Shopify BEST_SELLING), the product's
    // category, or the whole store - the last 30 days in better-sell.
    async showPicks() {
      this.showTyping();
      let intro = null;
      let products = [];
      let sources = {};
      if (this.isLoggedIn) {
        const data = await this.bsFetch('/api/personalized?limit=12');
        if (data?.personalized) {
          ({ products, sources } = await this.bsProductsToCards(data.products));
          if (products.length) intro = this.isArabic ? 'اخترنا لك هذه بناءً على مشترياتك:' : 'We picked these for you, based on what you shop:';
        }
      }
      const collection = this.pageContext.collection;
      if (!intro && collection) {
        products = await this.collectionBestSellers(collection.handle);
        sources = Object.fromEntries(products.map((p) => [p.id, 'best_selling']));
        if (products.length) intro = this.isArabic ? `الأكثر مبيعاً في ${collection.title}:` : `Best sellers in ${collection.title}:`;
      }

      if (!intro) {
        const query = this.contextProductId ? `?product=${encodeURIComponent(this.contextProductId)}&limit=12` : '?limit=12';
        // all-time store-wide list (api.personalized for guests) when the 30-day one is unavailable
        const data = (await this.bsFetch(`/api/chat-best-sellers${query}`))
          || (await this.bsFetch('/api/personalized?limit=12'));
        ({ products, sources } = await this.bsProductsToCards(data?.products));
        if (products.length) {
          intro = data?.scope === 'category'
            ? (this.isArabic ? 'الأكثر مبيعاً من هذا النوع حالياً:' : 'Best sellers like this one right now:')
            : (this.isArabic ? 'الأكثر مبيعاً الآن:' : 'Our best sellers right now:');
        }
      }

      // Stores where better-sell has no sales history yet (or it is unreachable):
      // Shopify's own best-selling order, so the chip never dead-ends.
      if (!intro) {
        products = await this.storeBestSellers();
        sources = Object.fromEntries(products.map((p) => [p.id, 'best_selling']));
        if (products.length) intro = this.isArabic ? 'الأكثر مبيعاً الآن:' : 'Our best sellers right now:';
      }

      this.hideTyping();
      const shown = products.filter((p) => p.handle !== this.pageContext.product?.handle).slice(0, 8);
      if (!intro || !shown.length) {
        this.addAssistantMessage(this.strings.noProducts);
        this.showCatalogLevel(this.catalog, 1, []);
        return;
      }
      this.addAssistantMessage(intro);
      this.addProductCards(shown, { bsSources: sources });
      shown.forEach((p) => this.bsTrack({ eventType: 'IMPRESSION', productId: p.id, source: sources[p.id] }));
      this.addQuickReplies([this.strings.chipHelp, this.strings.chipDeals, this.strings.chipSupportLabel]);
    }

    // Store-wide best sellers in Shopify's BEST_SELLING order (in stock only).
    async storeBestSellers() {
      try {
        const res = await fetch(`${this.shopUrl}/api/2024-10/graphql.json`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': this.storefrontToken },
          body: JSON.stringify({
            query: `{ products(first: 60, sortKey: BEST_SELLING) { nodes { handle title availableForSale } } }`
          })
        });
        if (!res.ok) return [];
        const data = await res.json();
        const titles = new Set();
        const handles = (data?.data?.products?.nodes || [])
          .filter((p) => {
            const key = String(p.title || '').trim().toLowerCase();
            if (!p.availableForSale || titles.has(key)) return false;
            titles.add(key);
            return true;
          })
          .map((p) => p.handle);
        return handles.length ? this.fetchProductsByHandles(handles.slice(0, 12)) : [];
      } catch {
        return [];
      }
    }

    // A collection's own best sellers, in Shopify's BEST_SELLING order (in stock only).
    async collectionBestSellers(handle) {
      try {
        const res = await fetch(`${this.shopUrl}/api/2024-10/graphql.json`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': this.storefrontToken },
          body: JSON.stringify({
            query: `query($handle: String!) {
              collection(handle: $handle) {
                products(first: 60, sortKey: BEST_SELLING) { nodes { handle title availableForSale } }
              }
            }`,
            variables: { handle }
          })
        });
        if (!res.ok) return [];
        const data = await res.json();
        // colours are often separate products with one title: show each title once
        const titles = new Set();
        const handles = (data?.data?.collection?.products?.nodes || [])
          .filter((p) => {
            const key = String(p.title || '').trim().toLowerCase();
            if (!p.availableForSale || titles.has(key)) return false;
            titles.add(key);
            return true;
          })
          .map((p) => p.handle);
        return handles.length ? this.fetchProductsByHandles(handles.slice(0, 12)) : [];
      } catch {
        return [];
      }
    }

    // After an add-to-cart from a chat card: what is bought with it (once per product).
    async showPairs(product) {
      if (!this.betterSell || !product?.id || this.pairsShownFor.has(product.id)) return;
      this.pairsShownFor.add(product.id);
      const turn = this.turn;
      const productId = String(product.id).split('/').pop();
      const data = await this.bsFetch(`/api/chat-pairs?product=${encodeURIComponent(productId)}&limit=8`);
      const { products, sources } = await this.bsProductsToCards(data?.products);
      const shown = products.filter((p) => p.id !== product.id).slice(0, 6);
      // The customer moved on (new message, or a reply is being built): stay quiet.
      if (!shown.length || turn !== this.turn || this.isProcessing) return;
      this.addAssistantMessage(this.isArabic ? 'يتناسب معه:' : 'Goes well with it:');
      this.addProductCards(shown, { bsSources: sources });
      shown.forEach((p) => this.bsTrack({ eventType: 'IMPRESSION', productId: p.id, source: sources[p.id] }));
    }

    // --- Storefront API search ---


    // --- Collection cards from the menu catalog --------------------------------
    // Shopify's collection search returns the first four title matches in
    // creation order, often without an image and never for Arabic titles. The
    // menu catalog (the chat's own L1/L2/L3 sections, with card art in both
    // languages) is matched first; Shopify's results only fill the gaps.

    catalogIndex() {
      const out = [];
      const walk = (items, level, parent) => (items || []).forEach((it) => {
        if (it && it.title && it.handle) {
          out.push({ title: it.title, handle: it.handle, image: it.art || (this.isArabic && it.banner_ar) || it.banner || it.image || it.icon || '', level, parentTitle: parent ? parent.title : '' });
        }
        walk(it && it.children, level + 1, it);
      });
      walk(this.catalog, 1, null);
      return out;
    }

    matchCatalogCollections(q, limit) {
      if (!q || !q.hasProduct) return [];
      const norm = (s) => this.latinDigits(String(s || '')).toLowerCase()
        .replace(/t[\s-]?shirts?/g, 'tshirt')
        .replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim();
      const aliases = {
        shoe: ['footwear'], shoes: ['footwear'], sneaker: ['footwear', 'sneakers'], sneakers: ['footwear'], boot: ['footwear', 'boots'], boots: ['footwear'],
        sandal: ['footwear', 'sandals'], sandals: ['footwear'], slipper: ['footwear', 'slippers'], slippers: ['footwear'], trainers: ['footwear', 'sneakers'],
        clothes: ['apparel', 'clothing'], clothing: ['apparel'], outfit: ['apparel'], tee: ['tshirt'], tees: ['tshirt'],
        'حذاء': ['أحذية', 'احذية'], 'احذية': ['أحذية'], 'جزمة': ['أحذية'], 'كوتشي': ['أحذية'], 'سنيكرز': ['أحذية'], 'ملابس': ['الملابس'],
        'تيشيرت': ['تيشيرتات', 'تي شيرت'], 'بنطلون': ['بناطيل', 'بنطال'], 'بنطال': ['بناطيل'], 'عطر': ['عطور'], 'قميص': ['قمصان'], 'فستان': ['فساتين'],
        'شنطة': ['شنط', 'حقائب'], 'حقيبة': ['حقائب'], 'جاكيت': ['جاكيتات'], 'ساعة': ['ساعات'], 'نظارة': ['نظارات'], 'منشفة': ['مناشف'], 'مخدة': ['مخدات'], 'بطانية': ['بطانيات']
      };
      const genderWords = {
        boys: ['boys', 'boy', 'ولادي', 'ولادى', 'أولاد', 'اولاد', 'ولاد', 'صبيان'], girls: ['girls', 'girl', 'بناتي', 'بناتى', 'بنات'],
        men: ['men', 'mens', 'man', 'gents', 'رجالي', 'رجالى', 'رجال'], women: ['women', 'womens', 'woman', 'ladies', 'lady', 'نسائي', 'نسائى', 'نساء', 'حريمي', 'سيدات'],
        kids: ['kids', 'kid', 'children', 'child', 'boys', 'girls', 'أطفال', 'اطفال', 'طفل', 'ولادي', 'بناتي'], baby: ['baby', 'babies', 'infant', 'بيبي', 'رضع', 'رضيع', 'مواليد']
      };
      const allGender = new Set(Object.values(genderWords).flat());
      // Short function words never name a product; Arabic ones would otherwise
      // substring-match titles ("لم" inside "الملابس") and hijack support messages.
      const stop = /^(i|a|an|the|for|in|of|to|me|my|want|need|show|find|looking|please|some|any|with|and|new|not|has|have|is|are|was|it|its|أريد|اريد|ابي|أبي|أبغى|ابغى|بدي|عايز|عاوز|محتاج|لو|سمحت|من|فضلك|لي|في|على|مع|و|أو|او|لم|ما|هو|هي|كم|يا|ده|دي|مش|لا|ان|إن|عن|له|بس|لك|هذا|هذه|هل|إلى|الى|عند|ثم|قد|كل|بعد|قبل|غير|بين|حتى|لن|عايزة|عاوزة|عايزه|عاوزه|عايزين|عاوزين|نفسي|نفسى|محتاجة|محتاجه|محتاجين|هات|هاتلي|هاتلى|وريني|ورينى|ورّيني|شوفلي|شوفلى|اديني|إديني|ادينى|عندكم|عندك|فيه|ممكن|اشوف|أشوف|اشتري|أشتري|اشترى|أشترى|اجيب|أجيب|ليا|بتاع|بتاعة|بتاعت|علشان|عشان|كده|كدا|كدة|يعني|يعنى|حاجة|حاجه|اي|أي|ايه|إيه)$/i;
      const esc = (w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const stem = (w) => (w.length > 4 && /es$/.test(w) ? w.slice(0, -2) : (w.length > 3 && /s$/.test(w) ? w.slice(0, -1) : w));
      // Latin: whole words, either number. Arabic: whole words allowing the
      // article/prefix (ال, لل, و, ب) and common suffixes; no bare substrings.
      const inText = (text, word) => (/^[a-z0-9]/.test(word)
        ? new RegExp(`(^|\\s)${esc(stem(word))}(s|es|ses)?(\\s|$)`).test(text)
        : new RegExp(`(^|\\s)(و|ب|ل|ال|وال|بال|لل)?${esc(word)}(ات|ة|ين|ي)?(\\s|$)`).test(text));
      const tokens = norm(q.cleaned).split(' ').filter((t) => !stop.test(t) && !allGender.has(t) && (/^[a-z0-9]/.test(t) ? t.length >= 2 : t.length >= 3));
      if (!tokens.length) return [];
      const wanted = genderWords[q.gender] || null;
      const others = q.gender ? Object.keys(genderWords).filter((g) => g !== q.gender && g !== 'kids' && g !== 'baby').flatMap((g) => genderWords[g]) : [];
      const seen = new Set();
      const scored = [];
      this.catalogIndex().forEach((item) => {
        if (seen.has(item.handle)) return;
        const title = norm(item.title);
        const parent = norm(item.parentTitle);
        const handleText = norm(item.handle.replace(/-/g, ' '));
        const context = parent + ' ' + handleText;
        const titleWords = title.split(' ').filter(Boolean);
        const genderOnlyTitle = titleWords.length > 0 && titleWords.every((w) => allGender.has(w));
        let score = 0;
        tokens.forEach((t) => {
          const words = [t, ...(aliases[t] || [])].map(norm);
          if (words.some((w) => inText(title, w))) score += 1;
          // The product word may sit in the parent: "BOYS" under "FOOTWEAR"
          // counts in full, other parent matches half.
          else if (parent && words.some((w) => inText(parent, w))) score += genderOnlyTitle ? 1 : 0.5;
        });
        if (score === 0) return;
        const coverage = score / Math.max(1, titleWords.length);
        if (wanted) {
          if (wanted.some((w) => inText(title, w))) score += 2;
          else if (wanted.some((w) => inText(context, w))) score += 1;
          if (others.some((w) => inText(title, w) || inText(context, w))) score -= 1;
        }
        if (score <= 0) return;
        seen.add(item.handle);
        scored.push({ item, score, coverage, genderOnlyTitle, parentHasGender: parent.split(' ').some((w) => allGender.has(w)) });
      });
      // Best match, then the title most fully matched, then the broader section.
      scored.sort((a, b) => b.score - a.score || b.coverage - a.coverage || a.item.level - b.item.level || a.item.title.length - b.item.title.length);
      const picked = scored.slice(0, limit || 4);
      // Generic titles repeat across sections ("PANTS" x4) and gender-only
      // titles ("BOYS") say nothing alone: show the parent with them.
      const titleCount = {};
      picked.forEach(({ item }) => { titleCount[norm(item.title)] = (titleCount[norm(item.title)] || 0) + 1; });
      return picked.map(({ item, genderOnlyTitle, parentHasGender }) => {
        const dup = titleCount[norm(item.title)] > 1;
        const needsParent = item.parentTitle && (dup || genderOnlyTitle || parentHasGender) && !norm(item.title).includes(norm(item.parentTitle));
        return { title: needsParent ? `${item.parentTitle} · ${item.title}` : item.title, handle: item.handle, image: item.image ? { url: item.image } : null };
      });
    }

    // A menu section for a /collections/<handle> link, with its art, or null.
    catalogCardForUrl(url, label) {
      const match = /\/collections\/([a-z0-9][a-z0-9-]*)\/?(?:[?#]|$)/i.exec(String(url || ''));
      if (!match) return null;
      const item = this.catalogIndex().find((it) => it.handle === match[1] && it.image);
      return item ? { title: label || item.title, handle: item.handle, image: { url: item.image } } : null;
    }

    // The cards: catalog matches first, then Shopify's title matches (brand
    // collections and sub-collections the menu lacks); a Shopify match borrows
    // the catalog's art by handle. A card never shows without a photo: fewer
    // cards beat a grey placeholder.
    pickCollections(q, shopifyCollections, limit) {
      const max = limit || 4;
      const hasPhoto = (c) => !!(c && c.image && c.image.url);
      const picked = this.matchCatalogCollections(q, max).filter(hasPhoto);
      const seen = new Set(picked.map((c) => c.handle));
      const art = {};
      this.catalogIndex().forEach((it) => { if (it.image && !art[it.handle]) art[it.handle] = it.image; });
      (shopifyCollections || [])
        .filter((c) => c && c.handle && !seen.has(c.handle))
        .map((c) => (hasPhoto(c) || !art[c.handle] ? c : { ...c, image: { url: art[c.handle] } }))
        .filter(hasPhoto)
        .forEach((c) => { if (picked.length < max) { picked.push(c); seen.add(c.handle); } });
      return picked;
    }

    async searchAndShowProducts(searchTerms, filters, budget) {
      this.showTyping();
      const queryInfo = this.parseQuery(searchTerms.join(' '));
      // The API's Jev slot ("for my son", "لبنتي") beats a missing word here.
      if (filters && filters.gender && !queryInfo.gender) queryInfo.gender = String(filters.gender);

      try {
        const languageCode = this.isArabic ? 'AR' : 'EN';

        // Search collections and products in parallel
        const collectionQuery = searchTerms.join(' ');
        const [shopifyCollections, ...results] = await Promise.all([
          this.searchCollections(collectionQuery, languageCode),
          ...searchTerms.map((term) => this.storefrontSearch(term, languageCode, filters, budget))
        ]);

        const collections = this.pickCollections(queryInfo, shopifyCollections, 4);

        let allProducts = [];
        const seenIds = new Set();
        results.forEach((products) => {
          products.forEach((p) => {
            if (!seenIds.has(p.id)) {
              seenIds.add(p.id);
              allProducts.push(p);
            }
          });
        });

        // Client-side budget filter (+30% tolerance)
        if (budget && typeof budget === 'number') {
          const maxPrice = budget * 1.3;
          allProducts = allProducts.filter((p) => {
            const price = parseFloat(p.priceRange?.minVariantPrice?.amount || 0);
            return price <= maxPrice;
          });

          if (allProducts.length === 0) {
            const fallbackQueries = searchTerms.map((term) => this.storefrontSearch(term, languageCode, filters, null));
            const fallbackResults = await Promise.all(fallbackQueries);
            const fallbackProducts = [];
            const fallbackSeen = new Set();
            fallbackResults.forEach((products) => {
              products.forEach((p) => {
                if (!fallbackSeen.has(p.id)) {
                  fallbackSeen.add(p.id);
                  fallbackProducts.push(p);
                }
              });
            });

            if (fallbackProducts.length > 0) {
              const cheapest = fallbackProducts.reduce((min, p) => {
                const price = parseFloat(p.priceRange?.minVariantPrice?.amount || Infinity);
                return price < min ? price : min;
              }, Infinity);
              const cheapestInt = Math.ceil(cheapest);
              const currency = this.currencySymbol;
              const msg = this.isArabic
                ? `لم نجد منتجات بميزانية ${budget} ${currency}. أقل سعر متوفر هو ${cheapestInt} ${currency}. هل تود زيادة الميزانية؟`
                : `No products found within ${budget} ${currency}. The lowest price available is ${cheapestInt} ${currency}. Would you like to increase your budget?`;
              this.hideTyping();
              this.addAssistantMessage(msg);
              const yesLabel = this.isArabic ? `عرض من ${cheapestInt} ${currency}` : `Show from ${cheapestInt} ${currency}`;
              const noLabel = this.isArabic ? 'بحث آخر' : 'Search something else';
              this.addQuickReplies([yesLabel, noLabel]);
              return;
            }
          }
        }

        this.hideTyping();

        // Show matching collections first
        if (collections.length > 0) {
          this.addCollectionCards(collections);
        }

        if (allProducts.length === 0 && collections.length === 0) {
          // Tier 2: Broadened Storefront search
          const broaderTerms = this.generateBroaderTerms(searchTerms);
          if (broaderTerms.length > 0) {
            const broaderResults = await Promise.all(
              broaderTerms.map((t) => this.storefrontSearch(t, languageCode, filters, budget))
            );
            broaderResults.forEach((products) => {
              products.forEach((p) => {
                if (!seenIds.has(p.id)) {
                  seenIds.add(p.id);
                  allProducts.push(p);
                }
              });
            });
          }

          // Tier 3: Semantic Pinecone search
          if (allProducts.length === 0) {
            const semanticQuery = searchTerms.join(' ');
            let semanticProducts = await this.semanticTextSearch(semanticQuery);
            const askedSize = this.normalizeSize(filters?.size);
            if (askedSize) semanticProducts = semanticProducts.filter((p) => this.hasSizeAvailable(p, askedSize));
            if (semanticProducts.length > 0) {
              allProducts = semanticProducts;
            }
          }

          // Show "similar products" message if fallback found results
          if (allProducts.length > 0) {
            const similarMsg = this.strings.similarProducts
              || (this.isArabic ? 'إليك منتجات مشابهة وجدناها:' : 'Here are similar products we found:');
            this.addAssistantMessage(similarMsg);
          }
        }

        // Nothing in the asked size: offer the neighbouring sizes instead.
        const wantSize = this.normalizeSize(filters?.size);
        let shownSize = wantSize;
        if (allProducts.length === 0 && wantSize && /^\d/.test(wantSize)) {
          const n = parseFloat(wantSize);
          const nearby = Number.isInteger(n) ? [n - 1, n + 1] : [Math.floor(n), Math.ceil(n)];
          for (const near of nearby.map(String)) {
            const nearResults = await Promise.all(
              searchTerms.map((t) => this.storefrontSearch(t, languageCode, { ...filters, size: near }, budget))
            );
            nearResults.forEach((products) => {
              products.forEach((p) => {
                if (!seenIds.has(p.id)) { seenIds.add(p.id); allProducts.push(p); }
              });
            });
            if (allProducts.length > 0) { shownSize = near; break; }
          }
          if (allProducts.length > 0) {
            this.addAssistantMessage(this.isArabic
              ? `المقاس ${this.arabicDigits(wantSize)} غير متوفر حالياً، هذه الخيارات بمقاس ${this.arabicDigits(shownSize)}:`
              : `Size ${wantSize} is sold out right now. Here is what we have in size ${shownSize}:`);
          }
        }

        if (allProducts.length === 0) {
          this.addAssistantMessage(wantSize
            ? (this.isArabic ? `لا توجد منتجات متوفرة بمقاس ${this.arabicDigits(wantSize)} حالياً.` : `Nothing in size ${wantSize} is in stock right now.`)
            : this.strings.noProducts);
          return [];
        }

        // Show product preview carousel, best converters first among the top matches
        this.showTyping();
        const shown = (await this.rankProducts(allProducts.slice(0, 16))).slice(0, 8);
        this.hideTyping();
        this.addProductCards(shown, { preferredSize: shownSize });

        // "View all" link to first matching collection
        if (collections.length > 0) {
          this.addViewAllLink(collections[0]);
        }
        return shown;
      } catch (err) {
        this.hideTyping();
        this.addAssistantMessage(this.strings.noProducts);
        return [];
      }
    }

    async storefrontSearch(term, languageCode, filters, budget) {
      let queryString = term;
      if (filters?.vendor) {
        queryString += ` ${filters.vendor}`;
      }
      // Add color/size to search query (more flexible than variant filters)
      if (filters?.color) {
        queryString += ` ${filters.color}`;
      }
      // A size is applied to the variants below, not typed into the search;
      // one the normaliser cannot read ("EU 35", "large") stays search text.
      const wantSize = this.normalizeSize(filters?.size);
      if (filters?.size && !wantSize) {
        queryString += ` ${filters.size}`;
      }

      const productFilters = [];
      if (budget && typeof budget === 'number') {
        productFilters.push({ price: { max: budget * 1.3 } });
      }
      if (wantSize) {
        // Server-side first: only products with a variant in that size and
        // something in stock, so the 24 results are not the 24 most relevant
        // adult shoes. The option is named "Size" on this store even on /ar;
        // Translated storefronts differ per store: KSA /ar names the option
        // "المقاس" and writes the values in Arabic-Indic digits, Kuwait /ar keeps
        // "Size" and "35". Same-type filters are OR-ed and unknown ones ignored.
        const sizeAr = this.isArabic ? wantSize.replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[+d]) : null;
        ['Size', 'مقاس', 'المقاس'].forEach((name) => {
          productFilters.push({ variantOption: { name, value: wantSize } });
          if (sizeAr && sizeAr !== wantSize) productFilters.push({ variantOption: { name, value: sizeAr } });
        });
        productFilters.push({ available: true });
      }

      const query = `
        query($query: String!, $first: Int!, $language: LanguageCode!, $productFilters: [ProductFilter!]) @inContext(language: $language) {
          search(query: $query, first: $first, types: PRODUCT, productFilters: $productFilters) {
            edges {
              node {
                ... on Product {
                  id
                  title
                  handle
                  vendor
                  availableForSale
                  featuredImage {
                    url
                    altText
                  }
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  compareAtPriceRange {
                    minVariantPrice {
                      amount
                    }
                  }
                  options {
                    name
                    optionValues {
                      name
                      swatch {
                        color
                      }
                    }
                  }
                  variants(first: 100) {
                    edges {
                      node {
                        id
                        availableForSale
                        selectedOptions {
                          name
                          value
                        }
                        price {
                          amount
                          currencyCode
                        }
                        compareAtPrice {
                          amount
                        }
                        image {
                          url
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const res = await fetch(`${this.shopUrl}/api/2024-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken
        },
        body: JSON.stringify({
          query,
          variables: {
            query: queryString,
            first: wantSize ? 24 : 8,
            language: languageCode,
            productFilters: productFilters.length > 0 ? productFilters : null
          }
        })
      });

      if (!res.ok) return [];

      const data = await res.json();
      const edges = data?.data?.search?.edges;
      if (!edges) return [];

      const products = edges.map((e) => e.node);
      return wantSize ? products.filter((p) => this.hasSizeAvailable(p, wantSize)) : products;
    }

    // --- Broader term generation ---

    generateBroaderTerms(searchTerms) {
      const broader = new Set();
      const originals = new Set(searchTerms.map((t) => t.toLowerCase()));

      searchTerms.forEach((term) => {
        const words = term.split(/\s+/).filter(Boolean);
        if (words.length < 2) return;

        // Drop one word at a time
        for (let i = 0; i < words.length; i++) {
          const sub = words.filter((_, j) => j !== i).join(' ');
          if (!originals.has(sub.toLowerCase())) broader.add(sub);
        }

        // Core noun (last word)
        const lastWord = words[words.length - 1];
        if (lastWord.length >= 3 && !originals.has(lastWord.toLowerCase())) {
          broader.add(lastWord);
        }
      });

      // Limit to 4 broader terms
      return [...broader].slice(0, 4);
    }

    // --- Semantic text search via Pinecone ---

    async semanticTextSearch(query) {
      try {
        const res = await fetch('https://kuwait-version6.vercel.app/api/text-search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, top_k: 20 })
        });

        if (!res.ok) return [];

        const data = await res.json();
        const handles = data?.products;
        if (!handles || handles.length === 0) return [];

        return this.fetchProductsByHandles(handles);
      } catch {
        return [];
      }
    }

    async fetchProductsByHandles(handles) {
      // Shopify handles are lowercase letters, digits, hyphens only — reject anything else
      const safeHandles = handles
        .slice(0, 20)
        .filter((h) => typeof h === 'string' && /^[a-z0-9-]+$/.test(h));

      if (safeHandles.length === 0) return [];

      const languageCode = this.isArabic ? 'AR' : 'EN';

      const productFields = `
        fragment ProductCardFields on Product {
          id
          title
          handle
          vendor
          availableForSale
          featuredImage {
            url
            altText
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
            }
          }
          options {
            name
            optionValues {
              name
              swatch {
                color
              }
            }
          }
          variants(first: 100) {
            edges {
              node {
                id
                availableForSale
                selectedOptions {
                  name
                  value
                }
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                }
                image {
                  url
                }
              }
            }
          }
        }
      `;

      const aliasQueries = safeHandles
        .map((handle, i) => `p${i}: product(handle: "${handle}") { ...ProductCardFields }`)
        .join('\n          ');

      const query = `
        ${productFields}
        query @inContext(language: ${languageCode}) {
          ${aliasQueries}
        }
      `;

      try {
        const res = await fetch(`${this.shopUrl}/api/2024-10/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken
          },
          body: JSON.stringify({ query })
        });

        if (!res.ok) return [];

        const data = await res.json();
        const root = data?.data;
        if (!root) return [];

        return safeHandles
          .map((_, i) => root[`p${i}`])
          .filter((p) => p && p.id);
      } catch {
        return [];
      }
    }

    // --- Collection search (Storefront API) ---

    async searchCollections(query, languageCode) {
      const gql = `
        query($query: String!, $first: Int!, $language: LanguageCode!) @inContext(language: $language) {
          collections(first: $first, query: $query) {
            edges {
              node {
                title
                handle
                image {
                  url
                  altText
                }
              }
            }
          }
        }
      `;

      try {
        const res = await fetch(`${this.shopUrl}/api/2024-10/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken
          },
          body: JSON.stringify({
            query: gql,
            variables: { query, first: 4, language: languageCode }
          })
        });

        if (!res.ok) return [];
        const data = await res.json();
        return data?.data?.collections?.edges?.map((e) => e.node) || [];
      } catch {
        return [];
      }
    }

    addCollectionCards(collections) {
      if (!collections || collections.length === 0) return;

      const container = document.createElement('div');
      container.className = 'chat-collections';

      const chevron = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>';

      collections.forEach((col) => {
        const a = document.createElement('a');
        a.className = 'chat-collection-card';
        a.href = `${this.langPrefix}/collections/${this.escapeHtml(col.handle)}`;

        let html = '';
        if (col.image?.url) {
          html += `<img class="chat-collection-card__image" src="${this.escapeHtml(col.image.url)}&width=88" alt="" loading="lazy" />`;
        } else {
          html += '<div class="chat-collection-card__icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg></div>';
        }
        html += `<div class="chat-collection-card__body"><span class="chat-collection-card__title">${this.escapeHtml(col.title)}</span><span class="chat-collection-card__arrow">${chevron}</span></div>`;
        a.innerHTML = html;
        container.appendChild(a);
      });

      this.messagesEl.appendChild(container);
      requestAnimationFrame(() => {
        this.autoShrinkTitles(container, '.chat-collection-card__title', 11);
      });
      this.scrollToBottom();
    }

    autoShrinkTitles(container, selector, minFontSize) {
      container.querySelectorAll(selector).forEach((el) => {
        let size = parseFloat(getComputedStyle(el).fontSize);
        while (el.scrollWidth > el.clientWidth && size > minFontSize) {
          size -= 0.5;
          el.style.fontSize = size + 'px';
        }
      });
    }

    addViewAllLink(collection) {
      const a = document.createElement('a');
      a.className = 'chat-view-all';
      a.href = `${this.langPrefix}/collections/${this.escapeHtml(collection.handle)}`;
      a.textContent = this.strings.browseAll;
      this.messagesEl.appendChild(a);
      this.scrollToBottom();
    }

    // --- Direct search: try Storefront API before AI fallback ---

    // "men" / "size 42" / "رجالي" / "٤٢" typed after a product search means
    // "<that search> for men / in 42", exactly like tapping the refine chip.
    // Without this, "men" after "shoes" searched the word "men" and showed
    // t-shirts.
    composeRefinement(last, q) {
      const gender = q.gender || last.gender;
      const size = q.size || last.size;
      const words = this.isArabic
        ? { boys: 'ولادي', girls: 'بناتي', men: 'رجالي', women: 'نسائي', kids: 'أطفال', baby: 'بيبي' }
        : { boys: 'boys', girls: 'girls', men: 'men', women: 'women', kids: 'kids', baby: 'baby' };
      const g = gender ? words[gender] : '';
      const sizePart = size ? (this.isArabic ? ` مقاس ${size}` : ` size ${size}`) : '';
      const main = this.isArabic ? `${last.base} ${g}` : `${g} ${last.base}`;
      return main.replace(/\s+/g, ' ').trim() + sizePart;
    }

    async tryDirectSearch(text, composed = false) {
      const q0 = this.parseQuery(text);
      const last = this.lastQuery;
      // A bare size only refines a shoes/clothing search ("backpack" then "42" still asks).
      const refines = q0.gender || (q0.size && last && (last.footwear || last.apparel));
      if (!composed && !q0.hasProduct && refines && last && last.base) {
        return this.tryDirectSearch(this.composeRefinement(last, q0), true);
      }
      const q = q0;
      const languageCode = this.isArabic ? 'AR' : 'EN';

      // A size and nothing to search for ("35", "مقاس ٣٥"): ask what it is for.
      if (q.size && !q.hasProduct) {
        this.hideTyping();
        this.addAssistantMessage(this.isArabic ? `مقاس ${this.arabicDigits(q.size)} لأي منتج؟` : `Size ${q.size} — shoes or clothing?`);
        this.addQuickReplies(this.isArabic
          ? [{ label: 'أحذية', query: `حذاء مقاس ${q.size}` }, { label: 'ملابس', query: `ملابس مقاس ${q.size}` }]
          : [{ label: 'Shoes', query: `shoes size ${q.size}` }, { label: 'Clothing', query: `clothing size ${q.size}` }]);
        return true;
      }

      const term = q.cleaned || text.trim();
      const filters = q.size ? { size: q.size } : {};

      // With a size, the full search handles sold-out sizes and the fallbacks.
      if (q.size) {
        this.hideTyping();
        this.lastSearchTerms = [term];
        this.lastSearchFilters = filters;
        if (q.hasProduct) this.lastQuery = q;
        const shown = await this.searchAndShowProducts([term], filters, null);
        this.addRefineChips(q, shown || []);
        this.addFollowUpChips();
        return true;
      }

      try {
        const [shopifyCollections, products] = await Promise.all([
          this.searchCollections(term, languageCode),
          this.storefrontSearch(term, languageCode, {}, null)
        ]);
        const collections = this.pickCollections(q, shopifyCollections, 4);

        if (collections.length === 0 && products.length === 0) {
          return false;
        }

        const shown = (await this.rankProducts(products.slice(0, 16))).slice(0, 8);
        this.hideTyping();
        this.lastSearchTerms = [term];
        this.lastSearchFilters = {};
        if (q.hasProduct) this.lastQuery = q;

        if (collections.length > 0) {
          this.addCollectionCards(collections);
        }

        if (shown.length > 0) {
          this.addProductCards(shown);
          if (collections.length > 0) {
            this.addViewAllLink(collections[0]);
          }
        }

        this.addRefineChips(q, shown);
        this.addFollowUpChips();
        return true;
      } catch {
        return false;
      }
    }

    // --- Order Tracking (client-side, no AI) ---

    async trackAndShowOrder(orderNumber, credential) {
      this.isProcessing = true;
      this.sendBtn.disabled = true;
      this.showTyping();

      try {
        const token = encodeURIComponent(`${orderNumber}-${credential}`);
        const res = await fetch(`${this.trackApiUrl}?token=${token}`);
        this.hideTyping();

        if (res.status === 403) {
          const msg = this.isArabic
            ? 'لم نتمكن من التحقق من بيانات الطلب. يرجى التأكد من رقم الطلب والبريد الإلكتروني أو رقم الهاتف.'
            : 'We couldn\'t verify your order details. Please check your order number and email/phone.';
          this.addAssistantMessage(msg);
          return;
        }
        if (res.status === 429) {
          this.addAssistantMessage(this.strings.rateLimited);
          return;
        }
        if (!res.ok) {
          this.addAssistantMessage(this.strings.error);
          return;
        }

        const data = await res.json();
        this.renderTrackingResult(data, `${orderNumber}-${credential}`);
      } catch (err) {
        this.hideTyping();
        this.addAssistantMessage(this.strings.error);
      } finally {
        this.isProcessing = false;
        this.sendBtn.disabled = false;
      }
    }

    renderTrackingResult(data, token) {
      const statusLabels = {
        Created: { en: 'Order Placed', ar: 'تم إنشاء الطلب' },
        Confirmed: { en: 'Confirmed', ar: 'تم التأكيد' },
        Assigned: { en: 'Assigned to Driver', ar: 'تم تعيين سائق' },
        PickedUp: { en: 'Picked Up', ar: 'تم الاستلام' },
        InTransit: { en: 'In Transit', ar: 'في الطريق' },
        OutForDelivery: { en: 'Out for Delivery', ar: 'خارج للتوصيل' },
        Delivered: { en: 'Delivered', ar: 'تم التوصيل' },
        Cancelled: { en: 'Cancelled', ar: 'ملغي' },
        Returned: { en: 'Returned', ar: 'مرتجع' }
      };

      const lang = this.isArabic ? 'ar' : 'en';
      const statusKey = data.deliveryStatus || 'Processing';
      const processingLabel = { en: 'Processing', ar: 'قيد المعالجة' };
      const currentStatus = statusLabels[statusKey]?.[lang] || processingLabel[lang];
      const badgeClass = data.deliveryStatus ? data.deliveryStatus.toLowerCase().replace(/[^a-z]/g, '') : 'created';

      let html = '<div class="chat-tracking">';
      html += '<div class="chat-tracking__header">';
      html += `<span class="chat-tracking__order">${this.escapeHtml(data.orderNumber)}</span>`;
      html += `<span class="chat-tracking__badge chat-tracking__badge--${badgeClass}">${this.escapeHtml(currentStatus)}</span>`;
      html += '</div>';

      const details = [];
      if (data.driverName && ['InTransit', 'OutForDelivery', 'Assigned'].includes(data.deliveryStatus)) {
        details.push(this.isArabic ? `السائق: ${data.driverName}` : `Driver: ${data.driverName}`);
      }
      if (data.shippingAddress?.city) {
        details.push(this.isArabic ? `المنطقة: ${data.shippingAddress.city}` : `Area: ${data.shippingAddress.city}`);
      }
      if (data.totalAmount && data.currency) {
        details.push(this.isArabic ? `المبلغ: ${data.totalAmount} ${data.currency}` : `Total: ${data.totalAmount} ${data.currency}`);
      }

      if (details.length > 0) {
        html += '<div class="chat-tracking__details">';
        details.forEach((d) => { html += `<div>${this.escapeHtml(d)}</div>`; });
        html += '</div>';
      }

      if (data.lineItems && data.lineItems.length > 0) {
        html += '<div class="chat-tracking__items">';
        data.lineItems.forEach((item) => {
          html += `<div class="chat-tracking__item">${this.escapeHtml(item.name)} × ${item.quantity}</div>`;
        });
        html += '</div>';
      }

      if (data.statusHistory && data.statusHistory.length > 0) {
        html += '<div class="chat-tracking__timeline">';
        data.statusHistory.forEach((step, i) => {
          const isLast = i === data.statusHistory.length - 1;
          const label = statusLabels[step.status]?.[lang] || step.status;
          const time = this.formatTrackingTime(step.timestamp);
          html += `<div class="chat-tracking__step${isLast ? ' chat-tracking__step--current' : ''}">`;
          html += '<span class="chat-tracking__dot"></span>';
          html += `<span class="chat-tracking__step-label">${this.escapeHtml(label)}</span>`;
          html += `<span class="chat-tracking__step-time">${this.escapeHtml(time)}</span>`;
          html += '</div>';
        });
        html += '</div>';
      }

      const trackUrl = `${this.trackUrlPrefix}/${encodeURIComponent(token)}`;
      const linkLabel = this.isArabic ? 'عرض تفاصيل التتبع' : 'View full tracking';
      html += `<a class="chat-tracking__link" href="${trackUrl}" target="_blank" rel="noopener">${this.escapeHtml(linkLabel)}</a>`;

      html += '</div>';

      const el = document.createElement('div');
      el.className = 'chat-bubble chat-bubble--assistant';
      el.innerHTML = html;
      this.messagesEl.appendChild(el);
      this.scrollToBottom();
    }

    formatTrackingTime(timestamp) {
      try {
        const date = new Date(timestamp);
        return date.toLocaleDateString(this.isArabic ? 'ar' : 'en-US', {
          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
      } catch {
        return timestamp;
      }
    }

    // --- Variant picker helpers ---


    // --- Query understanding: size, gender, product (no AI) --------------------
    // Shoppers type "size 35 shoes", "boys shoes", "حذاء مقاس ٣٥". Shopify search
    // treats "35" as text, so results ignored the size. These read the three
    // slots, search on the product words only and filter by the size in stock.

    latinDigits(s) {
      return String(s == null ? '' : s)
        .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
        .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0));
    }

    arabicDigits(s) {
      return this.isArabic ? String(s).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[+d]) : String(s);
    }

    isSizeOption(name) {
      return /^(size|sizes|مقاس|المقاس|حجم|الحجم)$/i.test(String(name || '').trim());
    }

    // "35", "٣٥", "35.0", "xl" -> "35" / "XL"; anything else -> null
    normalizeSize(value) {
      if (value == null || value === '') return null;
      const v = this.latinDigits(value).trim();
      if (/^\d{1,2}(\.\d)?$/.test(v)) return String(parseFloat(v));
      if (/^(xxs|xs|s|m|l|xl|xxl|xxxl|[2-9]xl)$/i.test(v)) return v.toUpperCase();
      return null;
    }

    // A variant sized "41/42" fits a shopper asking for 41 or 42.
    sameSize(variantValue, wanted) {
      const y = this.normalizeSize(wanted);
      if (!y) return false;
      return String(variantValue == null ? '' : variantValue).split('/').some((part) => this.normalizeSize(part) === y);
    }

    variantSize(variant) {
      const opt = (variant.selectedOptions || []).find((so) => this.isSizeOption(so.name));
      return opt ? opt.value : null;
    }

    hasSizeAvailable(product, size) {
      const variants = product.variants?.edges?.map((e) => e.node) || [];
      return variants.some((v) => v.availableForSale && this.sameSize(this.variantSize(v), size));
    }

    // Sizes with stock across a result set: the most common ones, shown ascending.
    sizesInStock(products, limit) {
      const count = new Map();
      products.forEach((p) => {
        const seen = new Set();
        (p.variants?.edges || []).forEach((e) => {
          const v = e.node;
          const s = v.availableForSale ? this.normalizeSize(this.variantSize(v)) : null;
          if (s && !seen.has(s)) { seen.add(s); count.set(s, (count.get(s) || 0) + 1); }
        });
      });
      const num = (s) => (/^\d/.test(s) ? parseFloat(s) : 1000);
      return [...count.entries()]
        .sort((a, b) => b[1] - a[1] || num(a[0]) - num(b[0]))
        .slice(0, limit || 8)
        .map((e) => e[0])
        .sort((a, b) => num(a) - num(b) || a.localeCompare(b));
    }

    parseQuery(text) {
      // Question marks and word-final commas/full stops are not part of a size ("size 35?"); "د.ك" keeps its dot.
      const raw = this.latinDigits(text).replace(/[?؟!]+/g, ' ').replace(/[.,،]+(?=\s|$)/g, '').replace(/\s+/g, ' ').trim();
      let cleaned = raw;
      let size = null;
      // Words that carry a size; a bare number is only a size next to one of these.
      const sizedWords = /(^|\s)(shoe|shoes|sneakers?|boots?|sandals?|slippers?|trainers?|footwear|shirt|t-?shirt|tee|polo|pants?|jeans|shorts|dress|skirt|jacket|hoodie|sweater|top|trousers|leggings|abaya|thobe|socks|حذاء|أحذية|احذية|جزمة|كوتشي|صندل|شبشب|بوت|سنيكرز|كوتشى|كوتشيات|كوتش|جزم|شباشب|صنادل|بوتات|صندلة|سليبر|سليبرز|سنيكر|شوز|ملابس|قميص|تيشيرت|بنطلون|بنطال|جينز|شورت|فستان|تنورة|جاكيت|هودي|بلوزة|ثوب|عباية|جوارب|تيشرت|تيشرتات|تيشيرتات|جاكت|جاكتات|جواكت|جيبة|ترنج|ترينج|ترنجات|ترينجات|شراب|شرابات|فانلة|فانلات|فانله|طقم|أطقم|اطقم|جلابية|جلاليب|اسدال|إسدال|بلوزه|هودى|هوديز|بنطلونات|شورتات|فساتين|بيجامات|بيجاما|بوكسر|بوكسرات|سويتشيرت|سويت شيرت)(\s|$)/i;
      const worded = raw.match(/(?:^|\s)(?:size|sz|مقاس|المقاس|قياس|مقاسه|مقاسها|نمرة|نمره|النمرة|النمره|سايز|السايز)\s*[:#]?\s*(\d{2}(?:\.5)?|xs|s|m|l|xl|xxl|[2-9]xl)(?=\s|$)/i);
      if (worded) {
        size = this.normalizeSize(worded[1]);
        cleaned = raw.replace(worded[0], ' ');
      } else {
        // A bare two-digit number is a size unless it follows a price word ("under 50").
        const bare = raw.match(/(?:^|\s)(\d{2}(?:\.5)?)(?=\s|$)/);
        const priceBefore = /(under|below|less than|cheaper than|max|maximum|up to|around|about|budget|for|أقل من|اقل من|بأقل من|باقل من|تحت|حوالي|بحدود|أرخص من|ارخص من|لحد|لغاية|في حدود|فى حدود|بحوالي|بحوالى|ميزانيتي|ميزانيتى|ميزانية|(^|\s)ب)\s*$/i;
        const unitAfter = /^\s*(kd|kwd|dinar|dinars|fils|sar|qar|bhd|egp|aed|le|د\.?ك|دينار|كد|ر\.?س|ر\.?ق|د\.?ب|ج\.?م|ريال|جنيه|جنية|جنيهات|ج|درهم|فلس|months?|years?|yrs?|y|pairs?|pack|pcs|pieces?|شهر|أشهر|شهور|سنة|سنوات|قطع|قطعة|زوج)(\s|$)/i;
        if (bare
            && !priceBefore.test(raw.slice(0, bare.index))
            && !unitAfter.test(raw.slice(bare.index + bare[0].length))) {
          const n = parseFloat(bare[1]);
          const alone = raw.replace(bare[0], '').trim() === '';
          if (n >= 16 && n <= 50 && (alone || sizedWords.test(raw))) {
            size = this.normalizeSize(bare[1]);
            cleaned = raw.replace(bare[0], ' ');
          }
        }
      }
      cleaned = cleaned.replace(/\s+/g, ' ').trim();

      const genders = {
        boys: /^(boy|boys|boy's|son|sons|nephew|grandson|ولد|أولاد|اولاد|للأولاد|للاولاد|ولادي|ولادى|صبيان|صبي|ابني|إبني|لابني|لإبني|ولدي|لولدي|ولاد|الولاد|للولاد|ابنى|إبنى|لابنى|لإبنى|واد|لواد|الواد|لولاد|للصبيان|صبيانى|صبياني)$/i,
        girls: /^(girl|girls|girl's|daughter|daughters|niece|granddaughter|بنت|بنات|للبنات|بناتي|بناتى|بنتي|لبنتي|ابنتي|لابنتي|البنات|بنتى|لبنتى|بنوتة|بنوته|لبنوتة|لبنوته|البنت|للبنت)$/i,
        men: /^(man|men|mens|men's|gents|husband|father|dad|brother|رجل|رجال|رجالي|رجالى|للرجال|زوجي|لزوجي|أبي|أخي|لأخي|لأبي|جوزي|جوزى|لجوزي|لجوزى|بابا|لبابا|أبويا|ابويا|لأبويا|لابويا|أخويا|اخويا|لأخويا|لاخويا|راجل|لراجل|الرجالة|رجالة|للرجالة|الرجال)$/i,
        women: /^(woman|women|womens|women's|ladies|lady|wife|mother|mom|mum|sister|نساء|نسائي|نسائى|سيدات|للسيدات|للنساء|حريمي|حريمى|زوجتي|لزوجتي|أمي|لأمي|أختي|لأختي|مراتي|مراتى|لمراتي|لمراتى|ماما|لماما|امي|لامي|اختي|اختى|لاختي|لاختى|ستات|للستات|الستات|الحريمي|الحريمى|للحريمي|للحريمى|السيدات|النساء|مدام|للمدام)$/i,
        kids: /^(kid|kids|children|child|أطفال|اطفال|للأطفال|للاطفال|طفل|عيال|العيال|للعيال|عيالي|عيالى|أطفالي|اطفالي|أطفالى|اطفالى|الأطفال|الاطفال|أطفالنا|اطفالنا)$/i,
        baby: /^(baby|babies|infant|بيبي|رضيع|رضع|مواليد|بيبيهات|البيبي|للبيبي|نونو|النونو|للنونو|نيوبورن|المواليد|للمواليد)$/i
      };
      const stop = /^(i|a|an|the|for|in|of|to|me|my|want|need|show|find|looking|please|some|any|with|and|new|أريد|اريد|ابي|أبي|أبغى|ابغى|بدي|عايز|عاوز|محتاج|لو|سمحت|من|فضلك|لي|في|على|مع|و|أو|او|ده|دي|دول|مش|لا|بس|يا|هو|هي|كم|عايزة|عاوزة|عايزه|عاوزه|عايزين|عاوزين|نفسي|نفسى|محتاجة|محتاجه|محتاجين|هات|هاتلي|هاتلى|وريني|ورينى|ورّيني|شوفلي|شوفلى|اديني|إديني|ادينى|عندكم|عندك|فيه|ممكن|اشوف|أشوف|اشتري|أشتري|اشترى|أشترى|اجيب|أجيب|ليا|بتاع|بتاعة|بتاعت|علشان|عشان|كده|كدا|كدة|يعني|يعنى|حاجة|حاجه|اي|أي|ايه|إيه)$/i;
      const footwear = /^(shoe|shoes|sneaker|sneakers|boot|boots|sandal|sandals|slipper|slippers|trainer|trainers|footwear|حذاء|أحذية|احذية|جزمة|كوتشي|صندل|شبشب|بوت|سنيكرز|كوتشى|كوتشيات|كوتش|جزم|شباشب|صنادل|بوتات|صندلة|سليبر|سليبرز|سنيكر|شوز)$/i;
      const apparel = /^(shirt|t-?shirt|tshirt|tee|polo|pants?|jeans|shorts|dress|skirt|jacket|hoodie|sweater|sweatshirt|top|trousers|leggings|abaya|thobe|pyjamas?|pajamas?|socks|ملابس|قميص|تيشيرت|بنطلون|بنطال|جينز|شورت|فستان|تنورة|جاكيت|هودي|بلوزة|ثوب|عباية|بيجامة|جوارب|تيشرت|تيشرتات|تيشيرتات|جاكت|جاكتات|جواكت|جيبة|ترنج|ترينج|ترنجات|ترينجات|شراب|شرابات|فانلة|فانلات|فانله|طقم|أطقم|اطقم|جلابية|جلاليب|اسدال|إسدال|بلوزه|هودى|هوديز|بنطلونات|شورتات|فساتين|بيجامات|بيجاما|بوكسر|بوكسرات|سويتشيرت)$/i;

      let gender = null;
      let hasProduct = false;
      let isFootwear = false;
      let isApparel = false;
      const baseTokens = [];
      cleaned.split(' ').filter(Boolean).forEach((tok) => {
        const t = tok.toLowerCase();
        const g = Object.keys(genders).find((k) => genders[k].test(t));
        if (g) { gender = gender || g; return; }
        if (stop.test(t)) return;
        baseTokens.push(tok);
        if (footwear.test(t)) isFootwear = true;
        if (apparel.test(t)) isApparel = true;
        if (t.replace(/[^\p{L}\p{N}]/gu, '').length >= 2) hasProduct = true;
      });

      // base: the product words alone ("shoes" from "men shoes size 42"), so a
      // later "women" or "41" can be combined with them.
      return { raw, cleaned, base: baseTokens.join(' '), size, gender, hasProduct, footwear: isFootwear, apparel: isApparel };
    }

    // Questions and support requests must reach the API: the product shortcut
    // would otherwise answer "I want to return an order" with product cards
    // because a product description happens to contain "return".
    looksLikeQuestion(text) {
      const t = String(text || '').trim();
      if (!t) return false;
      if (/[?؟]$/.test(t)) return true;
      // Trailing !/. must not hide an anchored token ("هيوصل امتى!").
      const u = t.replace(/[\s?؟!.،,]+$/, '');
      // Bare words that also name products (track pants, phone case, store bag)
      // are only questions in a phrase: "track my order", "phone number".
      const en = /(^|[^a-z])(how|when|where|why|what|which|who|can i|can you|could you|do you|does|is there|are there|i want to|i need to|i'd like to|i would like to|return|returns|refund|refunds|exchange|cancel|deliver|delivery|shipping|track(ing)? (my |the )?(order|shipment|package|parcel)|tracking number|my order|order (has|hasn't|has not|not|never|didn't|did not|is|was|still)|(hasn't|has not|not|never) (arrived|received|delivered|come)|(didn't|did not) (receive|get|arrive)|where is my (order|package|parcel|delivery)|opening hours|store hours|working hours|branch|branches|nearest store|store (location|locator)|address|phone number|contact|complaint|damaged|wrong item|late (delivery|order)|order is late|payment|how (do i|to|can i) pay|coupon|voucher|promo code|discount code|warranty|gift card|installment|installments|tabby|tamara)([^a-z]|$)/i;
      // Arabic has no \b: short words are anchored to spaces so they do not
      // fire inside product words (هل in هلو كيتي, وين in ويندبريكر, ساعات = watches).
      const arShort = /(^|\s)(هل|وين|ليش|كيف|متى|أين|ماذا|لماذا|ما هو|ما هي|طلبي|فين|ليه|ازاي|إزاي|ازى|امتى|إمتى|ايه|إيه|مين|بكام|كاش|فيزا|فاليو|فوري|قسط|اقساط|أقساط|ناقص|ناقصة|غلط|بايظ|بايظة|مكسور|مكسورة|مرتجع|كانسل|كنسل|الفلوس|فلوسي|فلوسى|اتأخر|اتاخر|موصلش|ماوصلش|مجاش|ماجاش|وصلش|جاش|استلمتش|مستلمتش|بتوصلوا|بتوصلو|بتشحنوا|بتشحنو|بيفتح|بيقفل|بتفتحوا|بتقفلوا|التليفون|محفظة|انستاباي|كونتكت|سهولة|ضمان|الدليفري|الديليفري|دليفري|ديليفري|اوردري|أوردري)(\s|$)/;
      const arPolite = /(^|\s)ممكن\s+(أعرف|اعرف|تساعد|تساعدني|أرجع|ارجع|ألغي|الغي|أتابع|اتابع|أستبدل|استبدل|أغير|اغير|أكلم|اكلم|أتكلم|اتكلم|أشتكي|اشتكي|أقسط|اقسط|أدفع|ادفع|ألغى|الغى|أعمل|اعمل|أرجّع|ارجّع)/;
      const arLong = /(أريد إرجاع|اريد ارجاع|ابي ارجع|أبي أرجع|إرجاع|ارجاع|استرجاع|استبدال|استرداد|إلغاء|الغاء|توصيل|التوصيل|الشحن|الشحنة|شحنتي|تتبع|الطلب|طلبي|الاوردر|الأوردر|اوردر|أوردر|ما وصل|لم يصل|ما استلمت|لم استلم|لم أستلم|وصلني|واصل|ساعات (العمل|الدوام)|أوقات (العمل|الدوام)|الدوام|فروع|الفروع|أقرب فرع|اقرب فرع|عنوان|رقم الهاتف|تواصل|شكوى|تالف|خطأ|متأخر|الدفع|كوبون|كود خصم|ضمان|تقسيط|تابي|تمارا|ما وصلش|لسه ما وصل|لسه موصلش|لسه ماوصلش|ما جاش|ما استلمتش|لسه مستلمتش|استرجاع الفلوس|ارجاع الفلوس|إرجاع الفلوس|رجوع الفلوس|عايز ارجع|عاوز ارجع|عايز أرجع|عاوز أرجع|عايزة ارجع|عاوزة ارجع|عايزة أرجع|عاوزة أرجع|عايز استبدل|عاوز استبدل|عايز أستبدل|عاوز أستبدل|عايزة استبدل|عاوزة استبدل|عايزة أستبدل|عاوزة أستبدل|اغير المقاس|أغير المقاس|تغيير المقاس|تغيير العنوان|اغير العنوان|أغير العنوان|اغير الرقم|أغير الرقم|تغيير الرقم|اغير اللون|أغير اللون|عايز الغي|عاوز الغي|عايز ألغي|عاوز ألغي|عايزة الغي|عاوزة الغي|عايزة ألغي|عاوزة ألغي|الغاء الطلب|إلغاء الطلب|الغاء الاوردر|إلغاء الأوردر|مصاريف الشحن|مصاريف التوصيل|مصاريف الدليفري|الدفع عند الاستلام|ماستر كارد|ماستركارد|فودافون كاش|اتصالات كاش|انستا باي|بالتقسيط|الفاتورة|فاتورة|ايصال|إيصال|مواعيد العمل|مواعيد الفروع|مواعيد الشغل|ساعات الشغل|رقم التليفون|نمرة التليفون|حاجة غلط|حاجة ناقصة|وصلني غلط|جاتلي غلط|جالي غلط|وصل غلط|برومو كود|بروموكود|كود الخصم|جيفت كارد|كارت هدايا|كارت هدية|كارت الهدايا|قسيمة|مرتجعات|سياسة الارجاع|سياسة المرتجعات)/;
      const ar = { test: (x) => arShort.test(x) || arPolite.test(x) || arLong.test(x) };
      return en.test(u) || ar.test(u);
    }

    // One follow-up at a time, as chips, never instead of results:
    // gender when it is missing, otherwise sizes in stock for footwear.
    addRefineChips(q, products) {
      if (!q || !q.hasProduct) return;
      const sizeWord = this.isArabic ? ' مقاس ' : ' size ';
      // Gender only matters for footwear and clothing (not "skechers" or "school bag").
      if (!q.gender && (q.footwear || q.apparel)) {
        const opts = this.orderByAudience(this.isArabic
          ? [['أولاد', 'ولادي', 'boys'], ['بنات', 'بناتي', 'girls'], ['رجال', 'رجالي', 'men'], ['نساء', 'نسائي', 'women']]
          : [['Boys', 'boys', 'boys'], ['Girls', 'girls', 'girls'], ['Men', 'men', 'men'], ['Women', 'women', 'women']]);
        const sizePart = q.size ? sizeWord + q.size : '';
        this.addAssistantMessage(this.isArabic ? 'لمن؟' : 'For whom?');
        this.addQuickReplies(opts.map(([label, word]) => ({
          label,
          query: this.isArabic ? `${q.cleaned} ${word}${sizePart}` : `${word} ${q.cleaned}${sizePart}`
        })));
        return;
      }
      if (!q.size && q.footwear && products && products.length) {
        const sizes = this.sizesInStock(products, 8);
        if (!sizes.length) return;
        this.addAssistantMessage(this.isArabic ? 'أي مقاس؟' : 'Which size?');
        this.addQuickReplies(sizes.map((s) => ({ label: this.arabicDigits(s), query: `${q.cleaned}${sizeWord}${s}` })));
      }
    }

    processVariantData(product, prefs) {
      const variants = product.variants?.edges?.map(e => e.node) || [];
      if (variants.length <= 1) return null;

      const options = product.options || [];
      // Skip "Title" option with only "Default Title" value
      const meaningful = options.filter(o => !(o.optionValues.length === 1 && o.optionValues[0].name === 'Default Title'));
      if (meaningful.length === 0) return null;

      // Classify options: color-like vs size-like
      const colorNames = /^(color|colour|colou?r|لون|اللون)$/i;
      const sizeNames = /^(size|sizes|مقاس|المقاس|حجم|الحجم)$/i;

      const sorted = [];
      const colorOpt = meaningful.find(o => colorNames.test(o.name));
      const sizeOpt = meaningful.find(o => sizeNames.test(o.name));
      if (colorOpt) sorted.push({ ...colorOpt, type: 'color' });
      if (sizeOpt) sorted.push({ ...sizeOpt, type: 'size' });
      // Add remaining options (up to 2 total)
      meaningful.forEach(o => {
        if (o !== colorOpt && o !== sizeOpt && sorted.length < 2) {
          // Guess type: if option has swatch data, treat as color
          const hasSwatch = o.optionValues.some(v => v.swatch?.color);
          sorted.push({ ...o, type: hasSwatch ? 'color' : 'size' });
        }
      });

      // Find first available variant for pre-selection
      const wantSize = prefs && prefs.preferredSize;
      const firstAvailable = (wantSize && variants.find(v => v.availableForSale && this.sameSize(this.variantSize(v), wantSize)))
        || variants.find(v => v.availableForSale) || variants[0];
      const selections = {};
      firstAvailable.selectedOptions.forEach(so => { selections[so.name] = so.value; });

      return { options: sorted, variants, selections };
    }

    findVariant(variants, selections) {
      return variants.find(v =>
        v.selectedOptions.every(so => selections[so.name] === so.value)
      ) || null;
    }

    updateChipAvailability(cardEl, variantData) {
      const { options, variants, selections } = variantData;

      options.forEach(opt => {
        const row = cardEl.querySelector(`.chat-variant-row[data-option="${CSS.escape(opt.name)}"]`);
        if (!row) return;

        row.querySelectorAll('.chat-variant-chip').forEach(chip => {
          const val = chip.dataset.value;
          // Check if any variant with this value is available given other selections
          const testSelections = { ...selections, [opt.name]: val };
          const matchingVariants = variants.filter(v =>
            v.selectedOptions.every(so =>
              testSelections[so.name] === undefined || testSelections[so.name] === so.value
            )
          );
          const anyAvailable = matchingVariants.some(v => v.availableForSale);

          chip.classList.toggle('chat-variant-chip--oos', !anyAvailable);
          const isSelected = selections[opt.name] === val;
          chip.classList.toggle('chat-variant-chip--selected', isSelected);
        });
      });
    }

    handleVariantChipClick(cardEl, chipEl, variantData) {
      const optionName = chipEl.closest('.chat-variant-row').dataset.option;
      const value = chipEl.dataset.value;

      // Update selection
      variantData.selections[optionName] = value;

      // Find matching variant
      const variant = this.findVariant(variantData.variants, variantData.selections);

      // Update chip highlights + cross-filter availability
      this.updateChipAvailability(cardEl, variantData);

      if (!variant) return;

      // Update ATC button variant GID
      const atcBtn = cardEl.querySelector('.chat-product-card__atc');
      if (atcBtn) {
        atcBtn.dataset.variantGid = variant.id;
        // Show/hide ATC based on availability
        if (variant.availableForSale) {
          atcBtn.style.display = '';
          atcBtn.disabled = false;
        } else {
          atcBtn.style.display = 'none';
        }
      }

      // Update card image if variant has a different image (color change)
      const optRow = variantData.options.find(o => o.name === optionName);
      if (optRow?.type === 'color' && variant.image?.url) {
        const imgEl = cardEl.querySelector('.chat-product-card__image');
        if (imgEl && imgEl.tagName === 'IMG') {
          imgEl.src = variant.image.url + '&width=300';
        }
      }

      // Update price display
      const priceEl = cardEl.querySelector('.chat-product-card__price');
      if (priceEl && variant.price) {
        let priceHtml = this.formatPrice(variant.price.amount);
        if (variant.compareAtPrice && parseFloat(variant.compareAtPrice.amount) > parseFloat(variant.price.amount)) {
          priceHtml += `<span class="chat-product-card__compare-price">${this.formatPrice(variant.compareAtPrice.amount)}</span>`;
        }
        priceEl.innerHTML = priceHtml;
      }

      // Update discount badge
      this.updateDiscountBadge(cardEl, variant);
    }

    cssColorFallback(name) {
      const map = {
        // English
        'black': '#000000', 'white': '#ffffff', 'red': '#e53935', 'blue': '#1e88e5',
        'navy': '#1a237e', 'navy blue': '#1a237e', 'green': '#43a047', 'yellow': '#fdd835',
        'orange': '#fb8c00', 'pink': '#e91e63', 'purple': '#8e24aa', 'grey': '#9e9e9e',
        'gray': '#9e9e9e', 'brown': '#6d4c41', 'beige': '#d7ccc8', 'cream': '#fffdd0',
        'gold': '#ffd700', 'silver': '#c0c0c0', 'maroon': '#800000', 'olive': '#808000',
        'teal': '#008080', 'coral': '#ff7043', 'burgundy': '#800020', 'charcoal': '#36454f',
        'khaki': '#c3b091', 'tan': '#d2b48c', 'ivory': '#fffff0', 'lavender': '#b39ddb',
        'mint': '#98ff98', 'peach': '#ffccbc', 'rose': '#ff8a80', 'turquoise': '#00bcd4',
        'wine': '#722f37', 'camel': '#c19a6b',
        // Arabic
        'أسود': '#000000', 'أبيض': '#ffffff', 'أحمر': '#e53935', 'أزرق': '#1e88e5',
        'كحلي': '#1a237e', 'أخضر': '#43a047', 'أصفر': '#fdd835', 'برتقالي': '#fb8c00',
        'وردي': '#e91e63', 'زهري': '#e91e63', 'بنفسجي': '#8e24aa', 'رمادي': '#9e9e9e',
        'بني': '#6d4c41', 'بيج': '#d7ccc8', 'كريمي': '#fffdd0', 'ذهبي': '#ffd700',
        'فضي': '#c0c0c0', 'عنابي': '#800020', 'زيتي': '#808000', 'فيروزي': '#00bcd4',
        'خمري': '#722f37', 'سماوي': '#87ceeb', 'نيلي': '#1a237e'
      };
      return map[name.toLowerCase().trim()] || null;
    }

    isLightColor(hex) {
      if (!hex) return false;
      const c = hex.replace('#', '');
      const r = parseInt(c.substring(0, 2), 16);
      const g = parseInt(c.substring(2, 4), 16);
      const b = parseInt(c.substring(4, 6), 16);
      // Perceived brightness (ITU-R BT.709)
      return (0.2126 * r + 0.7152 * g + 0.0722 * b) > 180;
    }

    updateDiscountBadge(cardEl, variant) {
      let badge = cardEl.querySelector('.chat-product-card__badge');
      if (variant.compareAtPrice && parseFloat(variant.compareAtPrice.amount) > parseFloat(variant.price.amount)) {
        const pct = Math.round((1 - parseFloat(variant.price.amount) / parseFloat(variant.compareAtPrice.amount)) * 100);
        if (pct > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'chat-product-card__badge';
            cardEl.insertBefore(badge, cardEl.children[1]);
          }
          badge.textContent = `-${pct}%`;
          return;
        }
      }
      if (badge) badge.remove();
    }

    // --- Utilities ---

    formatPrice(amount) {
      const num = parseFloat(amount);
      if (!Number.isFinite(num)) return '';
      // Decimal places follow the currency (KWD/BHD/OMR use 3, most others 2)
      const threeDecimal = ['KWD', 'BHD', 'OMR', 'JOD', 'TND', 'LYD'];
      const decimals = threeDecimal.includes((this.currency || '').toUpperCase()) ? 3 : 2;
      const [intPart, decimalStr] = num.toFixed(decimals).split('.');
      const sup = decimalStr ? `.${decimalStr} ${this.currencySymbol}` : ` ${this.currencySymbol}`;
      return `<span>${intPart}</span><sup>${sup}</sup>`;
    }

    isSafeUrl(url) {
      if (!url) return false;
      try { const u = new URL(url); return u.protocol === 'http:' || u.protocol === 'https:'; }
      catch { return false; }
    }

    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

  }

  customElements.define('chat-assistant', ChatAssistant);
}
